import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel, SubmitBar } from '@/components/studio'
import { Card, CardBody, CardHead, Badge, Button, Chips, CopyButton, DurationInput, Field, Progress, RatioInput, Segmented, Select, Textarea, isRatioValue } from '@/components/ui'
import { Icon } from '@/components/icons'
import { SmartImage } from '@/components/SmartImage'
import { fmtTime } from '@/lib/format'
import { RefUploader } from '@/components/RefUploader'
import { VideoAnalysisCard } from '@/components/VideoAnalysisCard'
import { AiFillButton } from '@/components/AiFillButton'
import { ModelSelect } from '@/components/ModelSelect'
import { TemplatePreview } from '@/components/TemplatePreview'
import { ImageLightbox } from '@/components/ImageLightbox'
import { SaveTemplateDialog, type TemplateFormValues, type TemplateShotLine } from '@/components/SaveTemplateDialog'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { DOMESTIC_PLATFORMS, OVERSEAS_PLATFORMS, getPlatform } from '@/config/platforms'
import { buildBoardShotsFillBrief, buildStoryboardPrompt, buildTemplateShotRefineBrief, buildVideoFillBrief, buildVideoPrompt, buildVideoPromptRefineBrief, effectiveStoryboard, storyboardInputKey, videoStoryboard, type VideoInput } from '@/config/prompts'
import {
  DEFAULT_VIDEO_MODE,
  DEFAULT_VIDEO_RESOLUTION,
  STUDIO_VIDEO_MODES,
  STUDIO_VIDEO_SLUGS,
  VIDEO_DURATIONS,
  VIDEO_RATIOS,
  VIDEO_RESOLUTIONS,
  VIDEO_STYLES,
  defaultsOf,
  getStudioVideoMode,
  getVideoMode,
  resolutionLabel,
  type VideoModeId,
} from '@/config/videoModes'
import {
  activeTemplate,
  storyboardWithEditedShots,
  templateShotLine,
  templateTitle,
  templatesForMode,
  type AnyVideoTemplate,
} from '@/config/videoTemplates'
import { useJobCenter, type Job } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { useVideoTemplates } from '@/context/VideoTemplatesContext'
import { useToast } from '@/context/ToastContext'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/api'
import { FILL_FORM_FIELDS, liveVideoAnalysis, type CustomVideoTemplate, type ProductAsset, type VideoAnalysis } from '@/api/types'
import { ProductInfoSection } from '@/components/ProductInfoSection'
import { mergeProductRefs, productImportMessage } from '@/lib/productRefs'
import { refillFromInputs } from '@/lib/refill'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'

type FormState = VideoInput & {
  /**
   * 当前档位。**写进草稿**，所以刷新/切页回来还在同一档；
   * 地址栏的 `?mode=` 优先于它（可分享 / 可回退，见页面顶部说明）。
   */
  mode: VideoModeId
  model: string
  /** 产品参考图（在「内容与场景」页）—— 还原产品外观，第一张是主图 */
  refs: string[]
  /**
   * 场景参考图（在「内容与场景」页）—— 控场景 / 氛围 / 构图。
   *
   * **与 `refs` 是两条通道，不是同一份数据的两个入口**：两者要回答的问题不同
   * （`refs` = 这个东西长什么样，`sceneRefs` = 这张片子长什么样），
   * 混成一个数组后用户在素材页传了产品图、到内容页又看到它，会以为传重了。
   * （两页的**序号随档位变**：合并页是第 2、3 位，复刻档是第 1、3 位 ——
   * 所以这里按页签名说，不按数字说，见 `PANE_BODIES` 上面那段。）
   * 提交时两者按「产品图在前、场景图在后」的顺序拼进同一个 `imageUrls`，
   * 并在提示词里逐段写明角色（见 `buildVideoPrompt` 的【参考图用法】）。
   */
  sceneRefs: string[]
  refVideo: string[]
  /**
   * 参考视频的**镜头结构** —— 复刻档第 1 页「分析视频」的产出。
   *
   * **存的是「当前生效的这一版」**：分析回来时写一份，用户在清单里改动就地覆盖。
   * 与分镜提示词同一条思路 —— 页面上只有一个「现在用的版本」，不并排摆
   * 「模型原版 / 手改版」两份（想回到模型原版就再点一次分析）。
   *
   * `videoUrl` 记的是**分析时用的那条片子**：换视频之后这份结构就过期了
   * （判据见 `analysisStale`）。不记的话，换一条新片子、旧结构还静静躺在提示词里，
   * 出片才发现复刻的是上一条的结构 —— 而那时钱已经花了。
   */
  videoAnalysis: VideoAnalysis | null
  platform: string
  /**
   * 清晰度。**必须与所选模型的能力匹配** —— 值取自模型的
   * `capabilities.resolutions`（原样大小写），换模型时由归一 effect 校正。
   */
  resolution: string
  /**
   * 分镜提示词的**手改版**（2026-09-30 按用户要求新增：「分镜提示词出来后能改，
   * 改完点生成分镜图」）。
   *
   * 空串 = 没改过，走 `buildStoryboardPrompt` 的自动版；
   * 非空 = 用户手写的那一份 —— 但它**只在 `sbInputKey` 与当前输入一致时才算数**。
   * 改过产品 / 模板 / 时长之后自动回到自动版，不会像以前那样永久压住自动版。
   *
   * 为什么存进草稿（`useDraft`）而不是页面自己的 state：它是一份**用户输入**，
   * 和产品名 / 卖点同类 —— 刷新要还在、「清空」要一起清掉。
   */
  storyboardPrompt: string
  /**
   * `storyboardPrompt` 与 `shotNotes` 是**为哪一组输入写的**（`storyboardInputKey`）。
   *
   * ## 这一格就是 2026-10-05 那次报障的正解
   *
   * 手改版与镜头细化都是「针对某个产品 + 某个模板」写出来的，却存在**跨产品的
   * 页面级草稿**里。没有这一格时手改版会永久压住自动版 ——
   * 用户原话「为什么这个不会跟着之前的产品的信息改变的？」，
   * 第二次的截图更直白：选了「卡点换装」模板、分镜图还是上一个产品的水壶。
   *
   * ## 为什么是「输入指纹」而不是「草稿版本号」
   *
   * 上午那版草稿迁移（`__draftVersion`）当天就被撤了 —— **版本号是每次写盘都写的**，
   * HMR 保留旧 state 时一次 `persist` 就能把新版本号盖着旧值写下去，
   * 迁移从此永久免检。指纹的判据取自**当前输入**、不依赖任何落盘状态，
   * 所以 HMR / 手改 / 回滚都打不穿。它顺带还自愈了存量：
   * 旧草稿没有这一格（`''`），与任何非空指纹都不等 ⇒ 旧值自动失效、不需要迁移。
   */
  sbInputKey: string
  /**
   * 已经**随「生成视频」用掉**的那张分镜图的 URL。
   *
   * 用户要求原话（2026-10-05）：「分镜图片使用过就清空」。分镜图是**开拍前的核对物**，
   * 随片子交出去之后使命就结束了 —— 留着只会在下一条片子里被当成有效参考图
   * 再次带上（白占一张参考图额度，还会把上一条的构图带进新片子）。
   *
   * 判据取 URL 而不是「用过了」这个布尔：重新生成分镜图得到的是新 URL，
   * 于是新图自动恢复可用，不需要额外重置这一格。
   */
  sbUsedUrl: string
}

/** 与档位无关的默认值。档位相关的那几项由 `defaultsOf()` 给（见 config/videoModes.ts） */
const BASE_DEFAULTS = {
  productName: '',
  sellingPoints: '',
  style: '清爽电商棚拍',
  extra: '',
  model: DEFAULT_FAMILY_ID.video,
  refs: [] as string[],
  sceneRefs: [] as string[],
  refVideo: [] as string[],
  resolution: DEFAULT_VIDEO_RESOLUTION,
  /** 空 = 分镜提示词走自动版（见 `FormState.storyboardPrompt`） */
  storyboardPrompt: '',
  /**
   * 镜头细化描述（一行一段）。空 = 用模板自带的分镜。
   *
   * 为什么它是**表单字段**而不是藏在提示词里：AI 补完要看得见、要能改、
   * 要刷新还在 —— 藏在提示词正文里的话，用户只能对着一段长文本猜哪几句是 AI 写的。
   * 与 `storyboardPrompt` 同一类（都是「用户输入」），所以同样进草稿，
   * 也同样受 `sbInputKey` 的绑定约束。
   */
  shotNotes: [] as string[],
  /** 手改版/镜头细化所绑定的输入指纹。空 = 没有手改过任何东西（见 `FormState.sbInputKey`） */
  sbInputKey: '',
  /** 已经随视频生成用掉的分镜图 URL（见 `FormState.sbUsedUrl`） */
  sbUsedUrl: '',
  /**
   * 参考视频的镜头结构（复刻档第 1 页「分析视频」的产出）。
   *
   * `null` = 没分析过 —— 与空数组不是一回事：空数组是「分析过、但一个镜头都没有」，
   * 那种情况提示词里不该出现「参考结构」这一段。
   */
  videoAnalysis: null as VideoAnalysis | null,
}

/**
 * 合并页**三个**一层页签 + **每个页签都常驻**的提交栏（复刻档只剩两个，见下）。
 *
 * ## 合并页的顺序为什么是「输出参数 → 内容与场景 → 分镜参考图」（2026-09-30 改；引擎搬走后保持）
 *
 * 用户原话（附「输出参数」那一屏的截图，指它说应该放第一个 step）：
 * 「参数与生成 应该放第一个 step」。
 *
 * 上一版的顺序是「引擎与素材 → 内容与场景 → 分镜参考图 → 参数与生成」，
 * 病根在于**参数是后面所有产出的输入，却排在最后**：
 * 平台 / 风格 / 清晰度 / 比例 / 时长这几项决定分镜提示词长什么样
 * （见 `buildStoryboardPrompt`），可用户是在第 4 步才改它们 ——
 * 改完回去看第 3 步的分镜图，那张图已经是按旧参数出的了，
 * 而页面不说明这一点，看上去就是「这张分镜图怎么一直在、还跟我的片子不搭」
 * （用户原话「怎么一直在的？」）。参数前置之后，**先定参数、再出提示词、
 * 再出分镜图、最后生成视频**，页签正好就是这条流程。
 *
 * ⚠️ 2026-10-09：原第 2 页「引擎与素材」**整页撤掉**（引擎搬进「视频模板」卡、
 * 产品参考图搬进「内容与场景」），所以现在只剩三页 —— 顺序表见 `MERGED_PANE_ORDER`。
 *
 * ⚠️ 页签顺序一改（含页数增减），`step_${pageKey}_vN` 的版本号必须跟着升
 * （见下面的 `useLocalStorage`）—— 老进度里「第 2 页」的语义已经换了，
 * 沿用老 key 会让用户静默停在错的页签上。
 *
 * ## ⚠️ 视频复刻档的顺序与上面这条**不一样**，现在是三页（2026-10-09 第三轮）
 *
 * 沿革：「参考视频（该放）第一位」→「都说第一部分是分析视频，那些生成引擎跟产品
 * 在 step 2」→（2026-10-09 第二轮）「2 跟 4 合并吧」「我觉得一个框跟上传图片
 * 就可以」→（本次）「这个分镜图需要强制生成才行」「复刻档也要分镜」。
 *
 * 上面那条「参数前置」的理由（参数决定分镜提示词长什么样）**只对
 * 带货短视频 / 商品视频档成立**：那一档的输入物是文案与产品图，参考视频
 * 根本不存在，第一个要回答的问题就是「投哪个平台、多长、什么比例」。
 *
 * 复刻档正好相反 —— **必传参考视频**，它决定了这条片子怎么拍。所以复刻档是：
 * **分析视频（`analyze`）→ 分镜参考图（`storyboard`）→ 产品与要求（`brief`）**。
 * 生成引擎、复刻方式、产品名称、场景参考图与整页「输出参数」都被砍掉 ——
 * 时长 / 比例 / 清晰度 / 平台降级成页签外一行紧凑 chip（见页面顶部那排）。
 *
 * **第 1 页为什么独立成「分析视频」、而不是「上传框下面挂一行」**：
 * 传完片子之后，用户的第一个问题必然是**这条片子是怎么拍的**（几个镜头、多快、
 * 什么景别、什么光线）。这一步是一次**独立且计费的产出**（先花一次钱跑视觉模型、
 * 要等十几秒），它的结果（镜头结构清单）会直接进提示词、
 * 也是「产品与要求」那一页生成成片提示词时的**镜头骨架**。
 * 把它塞进上传框里，用户会以为它是免费附带的，也不会去核对镜头拆得对不对。
 *
 * ⚠️ **两档各有一张顺序表**：
 *   · `analyze` / `brief` 只有复刻档有 —— 合并页的参考视频是**可选**的，
 *     给它占一页会让「本来可以不传」的动作看着像必做；
 *   · `storyboard`（分镜参考图）**两档都有**（2026-10-09 起）—— 生成视频只认
 *     那张九宫格分镜图，复刻档也要先出一张（见 `REPLICATE_PANE_ORDER` 沿革）。
 * 两张表都**只写 id**，标题与描述仍单源取自 `PANES`（枚举单一真源）。
 * 同理，**正文不按下标取，按页签 id 取**（见 `PANE_BODIES`）：
 * 顺序与正文之间的那根线，从「同一个数字」换成「同一个 id」。
 *
 * 其余两条是上一轮定下的、这次没动：
 *
 * - **分镜参考图独占最后一个页签**（2026-09-29 新增）。用户原话是
 *   「你选择了模板或者类型，然后输出产品跟要求，第二步输出分镜图（9 宫格），
 *   最后再生成视频」—— 它是一次**独立的产出**（要花一次生图的钱、要等一次出图、
 *   不满意会重出），不是参数里的一行，所以给它单独一屏；「最后再生成视频」
 *   落到**右栏顶部**的提交栏，两者同屏。
 * - 右栏（结果区）**不随页签切换**，它是页面的实时反馈层 ——
 *   改任何一页的参数，结果立刻跟着变。
 *   ⚠️ 2026-10-09：「成片提示词」卡（原挂在右栏顶部）已搬进「生成视频」的
 *   确认弹窗，右栏不再常驻它。
 *
 * 引擎的去处（2026-10-09 起）：它不再占页签，而是落在**「视频模板」卡里、
 * 模板轨道下面一行**（`TemplateRail` 的 `engine` 插槽）。理由是它与模板同属
 * 「这条片子走哪条通道」这一层决策，摆在模板旁边用户一眼能看全；它仍不属于
 * 「输出参数」——那六项是**报价与画面规格**，引擎是**通道**。
 * 复刻档根本不渲染模板卡，所以那一档没有引擎下拉 —— 引擎退成模型清单里的
 * 默认项（`DEFAULT_FAMILY_ID.video`）。
 */
const PANES: WorkflowTab[] = [
  /* ⚠️ 每个条目都必须**写在一行里、且以对象字面量开头**：冒烟是按源码正则
     数「页签对象」的出现次数、再与 `PANE_BODIES` 的键集合比对的（见 smoke-nav）。
     写成多行对象会让它少数一项、报出「声明了 5 个页签却有 4 个键」的假失败。
     同样地，**注释里也别再写一遍那个字面量** —— 正则不剥注释，写一次就多算一个。 */
  { id: 'params', title: '输出参数', desc: '投放平台、视觉风格、清晰度、比例与时长 —— 这几项既决定成片长什么样，也决定这次花多少钱' },
  { id: 'analyze', title: '分析视频', desc: '先传参考视频（必传），让 AI 拆出它的镜头结构与节奏 —— 后面几页都按这份结构复刻' },
  /* 复刻档专属（2026-10-09 第二轮）：取代了上一版的「引擎与内容」+「输出参数」。
     用户原话「我觉得一个框跟上传图片就可以」—— 复刻档的输入只剩
     参考视频（第 1 页）、产品图与一个要求框（本页）。只在 `REPLICATE_PANE_ORDER`
     里出现 —— 合并页没有它，也不再有「引擎与素材」页（引擎已移出页签）。 */
  { id: 'brief', title: '产品与要求', desc: '传一张产品图，再写清这条片子要讲什么 —— AI 按参考视频拆出的镜头结构，把它们汇总成一版成片提示词' },
  { id: 'content', title: '内容与场景', desc: '产品名称、视频场景、卖点与场景参考图 —— 决定成片讲什么、画面长什么样' },
  { id: 'storyboard', title: '分镜参考图', desc: '改好分镜提示词，再出一张 3×3 九宫格核对镜头（带产品图 / 场景图）；生成视频只以它为参考图，「生成视频」按钮在每个页签都在（右栏顶部）' },
]

/**
 * **合并页**（带货短视频 / 商品视频）的页签顺序 —— 就是上面那张清单的原序。
 *
 * 单列出来的原因：两档的顺序从 2026-10-08 起分道扬镳，而「合并页用什么顺序」
 * 这件事必须有**一处**能读出来。不写它、让代码里继续用 `PANES` 兜底，
 * 那 `PANES` 就同时背着两个意思（「有哪些页」和「合并页按什么顺序」）——
 * 往后往 `PANES` 里加一张复刻档专属页，合并页会**悄悄多出一个页签**。
 *
 * ⚠️ 2026-10-09：`source`（引擎与素材）**整页撤掉** —— 引擎搬进「视频模板」卡、
 * 产品参考图搬进「内容与场景」页（用户口径「引擎放到视频模板那边」「产品参考图要
 * 放到内容跟场景」），那一页搬空后没有内容。合并页页签 4 → 3。
 */
const MERGED_PANE_ORDER = ['params', 'content', 'storyboard'] as const

/**
 * **视频复刻档**的页签顺序 —— 三页。
 *
 * 沿革：
 *  · 2026-10-08 第二轮：参考视频独立成第 1 页（`analyze`）。
 *  · 2026-10-09 第一轮：`source`（引擎与产品）与 `content`（内容与场景）合并成
 *    `engine` 一页（用户原话「2 跟 4 合并吧」），页签 4 个 → 3 个。
 *  · 2026-10-09 第二轮：**整页砍到 2 个**（`analyze` → `brief`）。用户原话
 *    「我觉得一个框跟上传图片就可以」「第一图的生成 AI 提示词，结构跟脚本
 *    要用分析视频的基础去生成」—— 删掉生成引擎、复刻方式、产品名称、
 *    场景参考图与整个「输出参数」页，输入只剩
 *    **参考视频（第 1 页）+ 产品图 + 一个要求框**；
 *    时长 / 比例 / 清晰度 / 平台降级成页签外的紧凑 chip 行（见页面顶部那排）。
 *  · 2026-10-09 第三轮（本次）：**中间插一页 `storyboard`**，回到 3 页。
 *    用户口径「这个分镜图需要强制生成才行」+「复刻档也要分镜」——
 *    生成视频只认那张九宫格分镜图，复刻档不能没有出分镜的入口。
 *    这一页与合并页**同一份 `stepStoryboard`**（连 `sbUrl` / `sbFresh` 都是同一套
 *    job 数据），所以复刻档也共用「分镜图必须先生成」这条关口（见 `sbGateReason`）。
 *    以前这条顺序表里**刻意排除** `storyboard`（「复刻的是既有结构，没有分镜可排」），
 *    那个口径已被本次取代 —— smoke-nav 里那条「复刻档不许有 storyboard」也跟着改了。
 *
 * 为什么「输出参数」能整页砍掉而不丢信息：它原本回答的
 * 「这条片子多长、什么比例、投哪个平台」在复刻档里都**由参考视频兜底**
 * （复刻它的镜头与时长），所以只剩「花费相关、仍想让人调」的四项，
 * 排成一行 chip 比占一页更贴近「一次只回答一个问题」。
 *
 * 只写 id，标题与描述仍由 `PANES` 单源提供 —— 这里再抄一份文案的话，
 * 改名时必然漏掉一处（铁律：枚举单一真源）。
 *
 * ⚠️ 每一项都必须能在 `PANES` 里找到：写错一个 id，那个页签会**静默消失**
 * （页面照常渲染、点不到报错），所以冒烟按这条钉死，见 smoke-nav 的「复刻档顺序」段。
 */
const REPLICATE_PANE_ORDER = ['analyze', 'storyboard', 'brief'] as const


/*
 * 这里原有两条「分档文案覆盖」：`REPLICATE_SOURCE_COPY` / `REPLICATE_CONTENT_COPY`
 * （复刻档下把「引擎与素材」改叫「引擎与产品」、「内容与场景」的说明去掉「视频场景」）。
 *
 * 2026-10-09 **两条一起删了** —— 不是嫌它们啰嗦，而是**合并之后它们没有承载对象**：
 * 复刻档的页签顺序不再包含 `source` / `content`，那两条覆盖永远匹配不到任何一项。
 * 留着就是「传了也没用」的死分支（铁律：下架能力要连实现与常量一起清），
 * 而且它们的存在会让人以为 `source` / `content` 在复刻档里还会出现。
 * 合并后那两页该叫什么，由 `PANES` 里那条 `engine` 一份说清。
 */

/**
 * 分镜参考图的 app_slug —— **与后端 `registry.APP_KIND` / `taxonomy._BY_SLUG`
 * 里的键逐字一致**（`storyboard`）。
 *
 * 为什么要单独一个 slug、而不是直接借用当前档位的 `short-video`：
 * `app_kind(slug)` 决定这次走哪条链路，档位 slug 会被判成 video → 拿视频模型
 * 去出一张图。所以它必须是一个**独立登记的图片类 slug**（后端已注册为 image）。
 * 反过来说，前端这里改一个字而后端没同步，表现是「任务提交成功、然后失败」，
 * 或者更糟 —— 用视频模型去画分镜图，按秒计费。
 */
const STORYBOARD_SLUG = 'storyboard'

/**
 * 出分镜图用的模型：固定取默认生图模型（duomi 直连，¥0.10/张）。
 *
 * 不给用户选：这一步的产出是**给人看的核对物**，不是交付图，模型换成更贵的
 * 那一档只是多花钱；而且每多一个下拉，分镜页就多一份要解释的东西。
 * 真要换模型，最后一步提交的那支成片才是值得挑引擎的地方。
 */
const STORYBOARD_MODEL = DEFAULT_FAMILY_ID.image

/**
 * 两版分镜提示词是不是**同一版**。
 *
 * 用途：判断手上那张九宫格是不是**按当前提示词**出的（见 `sbFresh`）。
 * 后端把前端上送的 `prompt` 原样落库（`routers/aigc.py` 的 `prompt=prompt`），
 * 所以拿库里那条任务的 `prompt` 与当前提示词逐字比就行 —— 不比较输入字段，
 * 因为**提示词才是那张图的实际输入**：同一组参数只要把提示词改了，出的图就不一样。
 *
 * 首尾空白不算改动（textarea 里多敲一个换行不该让一张图「过期」）。
 * 只比首尾而不是把内部换行也归一 —— 内部换行是用户能看见、能控制的东西，
 * 折行差异会真的改变发出去的文字，判成「同一版」才是撒谎。
 */
const samePrompt = (a: string | undefined, b: string) => (a ?? '').trim() === b.trim()

/**
 * 「没有镜头细化」的**稳定空数组**。
 *
 * 为什么要这个常量而不是就地写 `[]`：`sbNotes` 在未绑定时会退化成空数组，
 * 而 `useMemo(…, [form, tpl, sbNotes])` 里一个每次渲染新建的数组**恒不等于自己** ——
 * 那个 useMemo 就白做了，而它的结果被 `sbPrompt` / `sbFresh` 逐字依赖。
 */
const EMPTY_NOTES: string[] = []

/**
 * 视频模板行 —— 页签**外**的一排可点封面卡（2026-09-29 新增）。
 *
 * 为什么在页签外：它一次铺开的是**五个分属两个页签的参数**
 * （场景在「内容与场景」，风格 / 比例 / 时长在「输出参数」），
 * 塞进任何一个页签都会变成「点完还得自己去找另外几项改没改」。
 * 与档位 chip-row 同属根决策层 —— 先定做什么，再逐页填细节。
 *
 * 卡片为什么必须有图：模板卖的是「出来大概长什么样」。
 * 名字（「会动的服装」）说不清画面，封面一眼就能对上。
 * 封面是**真实摄影**（`scripts/template-assets.json` 用 duomi-image-2 生成），
 * 不是插画 —— 插画给不出「成片质感」这个信息，上一版模板中心的插画封面就栽在这。
 *
 * 播放量角标（92w 这种）是参考站的展示数据，**不是我们的数据**，
 * 只在有值的模板上渲染；商品视频那四条是功能性模板，没有也不硬编一个。
 */
function TemplateRail({
  mode,
  custom,
  current,
  onPick,
  onPreview,
  onSave,
  engine,
}: {
  mode: VideoModeId
  /**
   * 本账号自存的模板。**只负责传进来** —— 怎么合并、谁排前面、五元组撞车谁赢，
   * 全在 `templatesForMode()` 那一份实现里（页面不写第二份判断）。
   */
  custom: CustomVideoTemplate[]
  /** 当前命中的模板（派生，见 `activeTemplate`）。没有就全部不选中 */
  current?: AnyVideoTemplate
  onPick: (t: AnyVideoTemplate) => void
  /** 卡片上那颗播放按钮 —— 打开分镜预览抽屉（见 `components/TemplatePreview.tsx`） */
  onPreview: (t: AnyVideoTemplate) => void
  /**
   * 尾部那张「存为模板」卡。与分镜参考图页那颗按钮**是同一个入口**
   * （都调页面里的 `openSaveTemplate`）—— 两处各写一遍保存逻辑的话，
   * 一处记住带封面、另一处忘了带，症状是「从这儿存的模板卡片是灰的」。
   */
  onSave: () => void
  /**
   * 「生成引擎」下拉 —— 由页面传进来的插槽，渲染在模板轨道**下面一行**。
   *
   * 2026-10-09 按用户口径从「引擎与素材」页搬到这里（原话「引擎放到视频模板那边」）：
   * 引擎与模板同属**根决策**（都决定这台片子走哪条通道 / 什么形态），摆在一起
   * 用户一眼看全，也不必为了先选引擎而多翻一页。合并页专用 —— 复刻档不渲染
   * 这张卡（它的引擎退成默认模型，见 `stepBrief`）。
   */
  engine?: ReactNode
}) {
  const list = templatesForMode(mode, custom)
  const railRef = useRef<HTMLDivElement | null>(null)
  /**
   * 左右箭头此刻该不该出现。
   *
   * 初始 `{prev:false, next:false}` 同时表示「还没量」与「没有溢出」——
   * 两种情况都该**不显示**箭头，所以不拆成两个状态。
   */
  const [nav, setNav] = useState({ prev: false, next: false })

  /**
   * 按轨道的**真实溢出**决定箭头，而不是「模板数超过 N 张」——
   * 张数不决定溢不溢出（同一个张数，视口宽窄一变结论就反过来），
   * 只有 `scrollWidth / clientWidth` 这一对是真的。`±1px` 是给亚像素取整
   * 留的余量：不留的话滚到头那颗会闪。
   */
  const syncNav = useCallback(() => {
    const el = railRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    const prev = el.scrollLeft > 1
    const next = max > 1 && el.scrollLeft < max - 1
    /* 滚动时每帧都会调到这儿 —— 结论没变就**复用原对象**：
       每次都 `setNav({...})` 会换新引用，白白把整条轨道重渲染一遍。 */
    setNav((s) => (s.prev === prev && s.next === next ? s : { prev, next }))
  }, [])

  useEffect(() => {
    const el = railRef.current
    if (!el) return
    /*
     * 列表一变就回到轨道起点。
     *
     * 自存模板是**异步拉的**，且排在最前（见 `templatesForMode()` 的排序）——
     * 它插进来那一刻，浏览器会对这条吸附轨道**重新吸附**，把 `scrollLeft`
     * 直接顶掉一整张卡（真机实测 92px）。症状：刚进页面第一张卡就是半截的、
     * 左侧还平白多出一颗「上一组」箭头，而用户什么都没动过。
     * 列表变了就回起点：新卡本来就在最前，起点正是要看的那一头。
     */
    el.scrollLeft = 0
    syncNav()
    el.addEventListener('scroll', syncNav, { passive: true })
    /* 两种变化都要重算，且是**两种不同的变化**：
       · 轨道自己的盒子变宽变窄（窗口缩放、页签折叠把卡片撑开）→ ResizeObserver；
       · 卡片增删（用户存了新模板）改的是 `scrollWidth` 而不是盒子尺寸，观察不到，
         所以把 `list.length` 挂进依赖，重挂时补一次 `syncNav()`。 */
    const ro = new ResizeObserver(syncNav)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', syncNav)
      ro.disconnect()
    }
  }, [syncNav, list.length])

  /** 箭头翻**一屏**，不是一格 —— 一格一格点太慢；滚到末尾由浏览器自动夹住 */
  const page = (dir: -1 | 1) => {
    const el = railRef.current
    if (!el) return
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
  }

  if (!list.length) return null
  return (
    <div style={{ marginBottom: 12 }}>
      <Card>
        <CardHead
          title="视频模板"
          icon="sparkles"
          /* 原来这句话有**两处**：卡头一行 + 轨道下面再一行「模板只铺参数、不锁死 ……」，
             说的是同一件事（点一张铺五个参数、手动改即自定义）。合成一行就够 ——
             那条脚注白占 38px 纵向高度，而这正是用户抱怨的那件事。 */
          /*
            ⚠️ 2026-10-10 补「清晰度要另选」这半句 —— 产品经理视角自查发现的真问题：
            原文案写「一键铺好场景 / 风格 / 时长 / 比例」，而**清晰度与投放平台
            一个都不铺**（`preset` 里确实只有这四个字段），用户以为点一下全齐了，
            实际右栏报价里那项 1080P 是他从没选过的。这句小字是**唯一**说明
            「模板铺到哪为止」的地方，所以宁可让它长一点。
            （2026-10-10 起模板还会顺手换掉生成引擎 —— 见 `pickTemplate`，
            也一并写进来，免得「它到底动了什么」又成了需要猜的事。）
          */
          /*
            「存为模板」—— 用户口径（2026-10-08）「用户改完模板可以存储起来」。

            ## 它为什么在卡头右侧，而不是轨道尾部（2026-10-08 当天改过一次）

            第一版是轨道末尾一张与模板卡等大的虚线卡，用户看了直接说「太丑了」。
            两个原因，都不是调间距能救的：

            ① **宽度从来没被约束住**：`.vtpl` 上的 `flex: 1 1 auto` 是给
               `.vtpl-wrap` 内部用的，而那张卡是**轨道的直接 flex 子项**，
               于是它吃掉了轨道全部剩余宽度 —— 实测渲染成一个约 400px 宽的
               巨大空框，而不是设计里那张 84px 的卡。
            ② 就算把宽度钉死，「八张有画面的卡 + 一张空卡」里那个空框**本身**
               就是视觉噪音：它和旁边八张实心封面卡并排，看着像少加载了一张图。

            现在它是卡头右侧一颗普通按钮，与分镜参考图页那颗「存为模板」
            **逐字同一副样子**（`star` + outline）—— 同一个动作在全站只该有
            **一种呈现**，两处长得不一样，用户会以为它们干的是两件事。
            按钮点击也不会误触折叠：`CardHead` 的 `right` 槽位自带事件拦截。
          */
          right={
            <Button
              variant="outline"
              size="sm"
              icon="star"
              onClick={onSave}
              title="把当前这套参数、分镜脚本与全片约束存成模板，下次点一下就能铺开"
            >
              存为模板
            </Button>
          }
        />
        <CardBody>
          <div className="vtpl-caro">
            <div className="vtpl-rail" ref={railRef}>
              {list.map((t) => {
                const on = current?.id === t.id
                return (
                  /* 外壳管两件事：定宽（轨道里只有它是 flex 子项），以及给播放按钮当定位基准 */
                  <div className="vtpl-wrap" key={t.id}>
                    <button
                      type="button"
                      className="vtpl"
                      data-active={on}
                      /*
                        `data-custom` —— 「这条是自存模板」，**纯元数据，不参与任何样式或行为**。
                        加它是因为：轨道是「自存在前」，于是**第 1 张卡是谁取决于用户存没存过模板**。
                        `verify-templates.py` 原先按下标 `[0..7]` 取「那 8 条内置模板」，
                        有自存模板之后就一直取错（把自存那条当成内置第一条，还漏测了最后一条内置）。
                        现在脚本按这个属性把两者分开，不再依赖顺序。
                        ⚠️ 取值用 `|| undefined`：`data-custom={false}` 会渲染成 `data-custom="false"`，
                        属性**照样在**，选择器 `[data-custom]` 就把内置模板也框进来了。
                      */
                      data-custom={t.custom || undefined}
                      onClick={() => onPick(t)}
                      title={`${t.desc}\n\n${templateTitle(t)}\n${t.preset.scene} · ${t.preset.style} · ${t.preset.duration}s · ${t.preset.aspectRatio}`}
                    >
                      <span className="vtpl__cover">
                        <SmartImage src={t.cover} alt={templateTitle(t)} />
                        {t.views ? (
                          <span className="vtpl__views">
                            <Icon name="play" size={10} />
                            {t.views}
                          </span>
                        ) : null}
                        {on ? (
                          <span className="vtpl__tick">
                            <Icon name="check" size={12} strokeWidth={2.6} />
                          </span>
                        ) : null}
                      </span>
                      {/*
                        ⚠️ 卡面只印**名字**，不印「角标 · 名字」（2026-10-10 改）。
                        用户原话是截图圈着 `卡点换装 · 4 LOOK 极速…`、`大促直给 · 一份钱给多少`
                        这几张说「标题两行就截断」。

                        根因不是「名字太长」，是**卡宽只有 84px**（用户 2026-10-05 亲口要的
                        「这个要小点」，见 global.css），10px 字号一行只放得下 7 个字 ——
                        两行 14 字，而 `templateTitle()` 拼出来的「角标 · 名字」普遍 12-16 字，
                        角标白吃掉最前面那 4-5 个字，真正用来分辨的那截名字反而被截掉。

                        所以**把卡加宽是错的方向**（那等于把用户要的小卡改回去），
                        正确的一刀是：卡面这 14 个字全部留给名字（最长 10 字，两行绰绰有余）。
                        角标没有丢：悬停 `title` 第一行、抽屉标题「模板预览 · 角标 · 名字」、
                        模板中心那张卡都还是 `templateTitle()` 的完整拼法。
                      */}
                      <span className="vtpl__name">{t.name}</span>
                    </button>
                    {/*
                      预览播放按钮（2026-10-05 用户要求）。**不能塞进上面那颗 button** ——
                      button 套 button 是非法 HTML，浏览器会把它拆出去。所以做成兄弟节点、
                      绝对定位浮在封面上。只有正中那颗圆吃点击：点封面其余地方仍是选中模板。

                      文案说「会拍成什么样」而不是「分镜示例」—— 抽屉里放的是**真案例片**
                      （`public/templates/<id>.mp4`，有就播），没出片的模板才回落逐格播，
                      两种都是"这条模板会拍成什么样"，而"分镜示例"只覆盖了后者。
                    */}
                    <button
                      type="button"
                      className="vtpl__play"
                      onClick={() => onPreview(t)}
                      title="预览这条模板会拍成什么样"
                      aria-label={`预览 ${templateTitle(t)}`}
                    >
                      <span>
                        {/* 播放键用实心三角（Icon 默认是描边，这里按需覆盖）*/}
                        <Icon name="play" size={11} fill="currentColor" strokeWidth={0} />
                      </span>
                    </button>
                  </div>
                )
              })}
              {/*
                ⚠️ 这里原来还有一张「存为模板」卡（`.vtpl--new`，2026-10-08 当天删）。
                它已经挪去卡头右侧，**别再搬回轨道里** —— 原因两条都写在上面
                `CardHead right` 的注释里（宽度漏气 + 空框本身就是噪音）。
                它与分镜参考图页那颗按钮**仍然是同一个入口**（同一个 `onSave`
                → 页面里的 `openSaveTemplate`）：两处各写一遍保存逻辑的话，
                一处记住带封面、另一处忘了带，症状是「从这儿存的模板卡片是灰的」。
              */}
            </div>
            {/*
              左右翻页箭头（2026-10-09）。用户口径「这个滑动条不好……弄一个展开
              还是 slider，还是 2 行？」→ 三选一定了「1 行 + 左右箭头」。

              箭头替代的是那根**原生横条**（已加进 global.css 顶部「应用内滚动区」
              的隐形清单），**不是**替掉横向滚动本身 —— 滚轮 / 触控板照旧能滚。
              哪一方向真还有内容才渲染哪一颗，滚到头那颗就消失；因此没有溢出时
              两颗都不在，不会出现「点了没反应」的假按钮。

              ⚠️ 它们**不是轨道的子项**（是外壳 `.vtpl-caro` 的子项）：轨道有护栏
              「直接子项只许是 `.vtpl-wrap`」（见 global.css），那护栏正是为了防止
              有东西混进 flex 行里把宽度吃乱。
            */}
            {nav.prev ? (
              <button
                type="button"
                className="vtpl-nav vtpl-nav--prev"
                onClick={() => page(-1)}
                title="上一组模板"
                aria-label="上一组模板"
              >
                <Icon name="chevronLeft" size={14} strokeWidth={2.4} />
              </button>
            ) : null}
            {nav.next ? (
              <button
                type="button"
                className="vtpl-nav vtpl-nav--next"
                onClick={() => page(1)}
                title="下一组模板"
                aria-label="下一组模板"
              >
                <Icon name="chevronRight" size={14} strokeWidth={2.4} />
              </button>
            ) : null}
          </div>
          {/* 引擎 —— 与模板轨道隔一条分隔线，同卡不同行 */}
          {engine ? (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid hsl(var(--border))',
              }}
            >
              {engine}
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  )
}

/**
 * 视频工作台（合并页，视频复刻仍独立）。
 *
 * ## 合并了两轮（2026-09-29 合路由，2026-10-05 合档位）
 *
 * **第一轮（2026-09-29）**，用户原话「带货短视频跟商品视频合并」：
 * 合并前侧栏「视频」组有三条入口，其中两条是**同一件事** ——
 * 带货短视频（`/short-video`）与商品视频（`/main-video`）共用同一条操作路径
 * （引擎 → 素材 → 内容与场景 → 参数 → 提交），差别只有产出形态
 * （15 秒竖版带货 vs 8 秒方版展示）。那一轮合掉的是**路由**：
 * 两条侧栏入口与两份 `jobsFor` 键收敛成一条 `/video-studio`，
 * 档位在页外用 chip-row 切。与「主图 / 套图 / 详情」三页合一同一个动作。
 *
 * **第二轮（2026-10-05）**，用户看过一轮之后的判词是「这两个感觉差不多啊，
 * 需要合并」：chip-row 那排选择器也没必要留 —— 对照当时的模板清单他确实说对了，
 * 商品视频档的 `mv-01/03/04` 与短视频档的 `vt-08/vt-10` 是同一类拍法，
 * 两个 chip 切来切去看到的是差不多的东西。所以这一轮**连档位一起合掉**：
 * `config/videoModes.ts` 里删掉 `main-video` 条目，chip-row 因此不再渲染，
 * 模板并成一条轨道（重复的三条已删）。旧链接靠**回落**兼容，不是靠留僵尸档位 ——
 * `?mode=main-video` / 老书签 `/main-video` 进来自动落到主力档。
 *
 * 档位清单的唯一真源是 `config/videoModes.ts`（页面里不写第二份）。
 *
 * **视频复刻（`/video-replicate`）不并入** —— 用户原话「视频复刻不变」。
 * 它的输入物性质相反：**必传参考视频**，是复刻既有结构而不是从文案生成，
 * 所以仍是独立入口、独立路由、独立草稿与独立任务键（`video-replicate`）。
 * 它在模式清单里用 `standalone` 标出来。
 *
 * ## 结构迭代史（为什么现在是 3 个页签）
 *
 * **第一版 · 页签**：WorkflowTabs 两个页签（素材 / 参数）。
 * **第二版 · 扁平一屏**（2026-09-28 上轮）：用户驳回第一版，原话「视频平台改为
 * 这些简单，现在太复杂了，右边的结果部分占比过大」，配四张参考站截图。
 * 于是撤掉页签改一屏到底，右栏另在 `.studio` 里收紧（见 global.css 注释）。
 * **第三版 · 3 个页签**（2026-09-28 本轮）：用户反馈「左边的 form 太长了，
 * 操作不友好」。扁平一屏解决了右栏占比，代价是把「引擎卡片 + 参考素材 +
 * 8 个内容参数 + 提交栏」叠成一列，左栏要滚很久才能看到提交按钮。
 * 现在拆成三页，每屏只回答一个问题。
 *
 * **拆页时保留下来的两条结论**（不要因为改了页签数就丢掉）：
 *
 * 1. **引擎与素材同页**（拆页时的结论，**不是**「引擎必须是第 1 页」）。
 *    它决定参考图要不要传（有图走图生视频、没图走文生视频，稳定性差一档），
 *    也是唯一的「用哪个通道 / 什么单价」。
 *    ⚠️ 2026-09-30 页签重排后「第 1 页」是输出参数（见 PANES 的头注释）；
 *    2026-10-08 复刻档又反过来把「参考视频」放到第 1 位。
 *    ⚠️ 2026-10-09：引擎**整块移出页签**，落到「视频模板」卡里、模板轨道下面一行
 *    （合并页专用）。所以这条结论只剩后半句成立 —— 「引擎与素材同页」这个前提
 *    本身已随「引擎与素材」页撤销而消失；能力与单价一字未动。
 * 2. **提交栏常驻在右栏顶部，不随页签切换消失**（2026-10-08 起与全站一致）。
 *    撤掉分步向导的核心原因就是向导把「可提交状态」藏起来了 —— 走完前面几步
 *    才知道要花多少钱、按钮在哪里。
 *    ⚠️ 2026-09-30 页签重排后这条只剩前半句成立：报价与按钮**只在最后一个页签
 *    才出现**（用户口径，见下面 submitDock 的注释）—— 页签可以切，但报价不常驻。
 *
 * 另：引擎选择是**一行下拉**（2026-09-29 由卡片改回、2026-10-09 挪进「视频模板」卡，
 * 见 `TemplateRail` 的 `engine` 插槽注释）——
 * 单价写在选项文本里，通道看字段右侧徽标，不必再用一屏卡片去换这点信息。
 *
 * ## 一个被修掉的隐形问题
 *
 * - **「投放平台」以前根本不存在**：`buildVideoPrompt` 一直支持 `platform`，
 *   但调用方从没传过 → 提示词里的「投放 淘宝天猫」和【平台风格】永远是默认值，
 *   字段形同虚设。现在它是表单第一项，平台的风格关键词与通版尺寸都真的进了提示词。
 *
 * （原「运镜」那条笔记作废：2026-10-09 用户口径「去掉运镜这一项」，控件与提示词里
 *   的【运镜】参数已整项删除；模板各自的镜头脚本里那句「机位怎么走」是这条模板
 *   自己的分镜约束，保留在 `videoTemplates` 的 `shot` 文案里。）
 */
export function VideoStudio() {
  const { pathname } = useLocation()
  const [sp, setSp] = useSearchParams()

  /**
   * 视频复刻是**独立页**（用户原话「视频复刻不变」），模式由路径决定。
   * 合并页则相反：模式来自地址栏 `?mode=`（可分享 / 可回退），其次草稿。
   *
   * `pageKey` 把「一个页面」的两件事钉在一起：草稿键、任务键（读 `jobsFor` 与
   * 写 `sourcePage` 必须是同一个值，否则右栏恒「共 0 个」且本页历史整卡不渲染）。
   */
  const isReplicate = pathname.includes('video-replicate')
  const pageKey = isReplicate ? 'video-replicate' : 'video-studio'
  const initialMode: VideoModeId = isReplicate ? 'video-replicate' : DEFAULT_VIDEO_MODE

  const { value: form, patch, reset } = useDraft(pageKey, {
    ...BASE_DEFAULTS,
    ...defaultsOf(initialMode),
  })
  /**
   * 页签进度按**页面**存一份（合并前是按档位三份：切档等于换页，进度归零）。
   *
   * ⚠️ **key 必须带版本，且每次页签顺序/数量变了都要升**：
   * v1 → v2 是页签 3 个变 4 个（第 3 位换人）；
   * v2 → v3（2026-09-30）是**顺序重排**（「输出参数」从第 4 位提到第 1 位）；
   * v3 → v4（2026-10-08 上午）是**复刻档顺序重排**（复刻档第 1 位由「输出参数」
   * 换成「参考视频」）；
   * v4 → v5（2026-10-08 下午）是**复刻档多了一页** —— 参考视频从「引擎与素材」
   * 里独立出来成了第 1 页「分析视频」（3 页 → 4 页）。合并页页签没动，
   * 但两档共用这一个 key，一起升。
   * v5 → v6（2026-10-09）是**复刻档少了页** —— 复刻档从「分析视频 / 引擎与内容 /
   * 输出参数」三页砍成「分析视频 / 产品与要求」两页。合并页页签仍没动。
   * v6 → v7（2026-10-09）是**合并页少了页** —— 「引擎与素材」整页撤掉（引擎去
   * 「视频模板」卡、产品图并进「内容与场景」），合并页从 4 页砍成 3 页。复刻档没动，
   * 但两档共用这一个 key，一起升。
   * v7 → v8（2026-10-09）是**复刻档多了页** —— 中间插进「分镜参考图」一格
   * （生成视频只认那张九宫格分镜图，复刻档也要出），复刻档 2 页 → 3 页。
   *
   * 沿用老 key 的话，这次的位移是：复刻档原来停在 `step=1`（「产品与要求」）的用户，
   * 在新顺序里第 2 格换成了「分镜参考图」—— 页面照常渲染、只是停错了页，
   * 截图上完全看不出来（这类错位本项目已经踩过一次，见 smoke-nav 里 img-localize 那段）。
   */
  const [step, setStep] = useLocalStorage<number>(`step_${pageKey}_v8`, 0)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  // 生成成功后自动清空工作区（表单 + 上传素材 + 右栏本轮产出）—— 见 GenerateFlowOptions.workspace。
  // ⚠️ 「分镜参考图」那次提交带 `keepWorkspace: true`（见 `doStoryboard`）：它是辅助产物，
  //    成功时不该把刚填好的分镜表 / 产品信息清掉。
  const flow = useGenerateFlow({ workspace: { snapshot: form, reset } })
  const toast = useToast()
  /* 存完模板后「去模板中心」用它 —— 模板墙在首页（见 App.tsx 的路由注释），不新开页签 */
  const navigate = useNavigate()
  /**
   * 本账号自存的视频模板（用户口径：「用户改完模板可以存储起来」）。
   *
   * 只做三件事，别的都不碰：把它传给模板条与 `activeTemplate()`、
   * 在保存成功后刷新 —— 合并规则、排序、深浅拷贝都在 `config/videoTemplates.ts`
   * 与 Context 里，页面不写第二份判断。
   *
   * `tplReady` 只有一个用处：`?vtpl=` 深链要等它。自存模板是异步拉的，
   * 挂载那一刻必然还不在清单里，不等待就会被判成「这个 id 不存在」——
   * 用户从模板中心点自己的模板进来，看到的是「点了没反应」（且地址栏的
   * `vtpl` 已被删掉，刷新也回不来）。
   */
  const { items: customTpls, ready: tplReady, create: createVideoTpl, update: updateVideoTpl } = useVideoTemplates()
  /* 「AI 细化镜头」是按次计费的（后端真扣积分），成功后要刷顶栏余额 */
  const { refresh: refreshBalance } = useAuth()

  /** 当前档位：合并页认地址栏，认不出（或被手改成别的档）就沿用地里的草稿 */
  const mode: VideoModeId = isReplicate
    ? 'video-replicate'
    : getStudioVideoMode(sp.get('mode') ?? form.mode).id
  const meta = getVideoMode(mode)

  /**
   * 页签清单 —— 本页页签的**唯一真源**：页签栏、正文取用、提交栏判「是否最后一页」、
   * 跨页签文案点名，全都从这一份派生，页面里不许再算第二次「复刻档少一个」。
   *
   * 两档**各按自己那张顺序表**取：
   *   合并页 = `MERGED_PANE_ORDER`（输出参数 → 内容与场景 → 分镜参考图）
   *   复刻档 = `REPLICATE_PANE_ORDER`（分析视频 → 产品与要求）
   * 两张表**都是显式的**（合并页那份不再靠「`PANES` 原序」兜底）—— 理由见
   * `MERGED_PANE_ORDER` 的注释：让 `PANES` 同时背「有哪些页」和「合并页什么顺序」
   * 两个意思，往后加一张复刻档专属页时合并页会悄悄多出一项。
   *
   * **定义位置被提到各个 `stepXxx` 之前**，因为下面那些块里的跨页签文案要用
   * `paneTitle()` 点名「去哪一页」—— 顺序分档之后，「页签 2」在两个档下指向
   * 不同的页，写死数字就是给自己留一张迟早对不上的地图。
   */
  const tabs = useMemo(() => {
    const order = isReplicate ? REPLICATE_PANE_ORDER : MERGED_PANE_ORDER
    /* 顺序表里只有 id：对象本身仍是 `PANES` 里那一个（改名两档同时生效）。
       找不到的 id 由 `filter` 挡掉 —— 少一个页签，好过整页崩。 */
    return order
      .map((id) => PANES.find((p) => p.id === id))
      .filter((p): p is WorkflowTab => Boolean(p))
  }, [isReplicate])

  /**
   * 按 id 取某页签在**当前档位**下的标题，供跨页签文案使用。
   * 取不到时给空串而不是抛错：文案缺一个词，好过整页白屏。
   *
   * 复刻档不再需要一层「折叠映射」：它的两页（`analyze` / `brief`）都是自己独有的，
   * 而点名的跨页签文案（`paneTitle('analyze')`、`paneTitle('content')`、`paneTitle('brief')`）
   * 各自只出现在对应的那一档里 —— 按 id 直查即可。
   */
  const paneTitle = (id: string) => tabs.find((t) => t.id === id)?.title ?? ''

  /**
   * 当前所选模型的**能力卡片** —— 来自后端 registry（值抄自上游 catalog 实测）。
   *
   * 为什么必须拿它、而不是在页面里写死「支持 480/720/1080」：
   * 14 个贰春模型的能力**不一样** —— 只有 3 个同时支持 480/720/1080，
   * MiniMax-H3 只有 768P，运营商渠道那几档只有一个值；
   * 参考视频更是有 4 个模型完全不收。写死一份的话，
   * 用户会在界面上挑到一个该模型根本不支持的档位，提交时才被上游拒。
   */
  const { list: listModels } = useModels()
  const modelInfo = listModels('video').find((m) => m.id === form.model) ?? null
  /**
   * `undefined` = 后端没说（旧的网关模型），回落全局默认；
   * 非空数组 = 后端明确给了范围，**完全以它为准**。
   */
  const allowedRes = modelInfo?.capabilities?.resolutions
  const allowedRatios = modelInfo?.capabilities?.aspect_ratios
  /** 参考图上限：后端没说时沿用旧的 4 张 */
  const refImageMax = modelInfo?.capabilities?.ref_image_max ?? 4
  /** 参考视频上限：`0` 是**明确的不支持**（不是未知），据此把上传框摘掉 */
  const refVideoMax = modelInfo?.capabilities?.ref_video_max ?? 0

  /* ------------------------------------------------------------------ *
   * 从商品库带入（2026-10-09）
   *
   * 用户口径：「其他页面也可以引用这个商品库啊」。这一页带入
   * **产品名 + 卖点 + 产品图**。
   *
   * ⚠️ 这一页的参考图额度是**产品图与场景图共享**的（`refImageMax`，
   * 由模型能力下发），所以带入时的上限不是 `refImageMax`，而是
   * 「扣掉场景图已占之后剩下的那部分」—— 否则带入 4 张产品图会把用户
   * 已经调好的场景图挤出去，而画面上只看到场景图没了，完全联想不到是这一步干的。
   * 场景图优先保留：它是用户明确指定「只学场景与光线」的那一份。
   *
   * ⚠️ 同样不新增第二份 state：带入后就是普通手填值。
   * ------------------------------------------------------------------ */
  const applyProductAsset = (asset: ProductAsset) => {
    const room = Math.max(0, refImageMax - form.sceneRefs.length)
    const incoming = asset.images.map((i) => i.url).filter(Boolean)
    const { urls, dropped } = mergeProductRefs(form.refs, incoming, room)
    patch({ productName: asset.title, sellingPoints: asset.sellingPoints, refs: urls })
    toast.success(productImportMessage(asset.title, { refs: urls.length, dropped, max: room }))
  }

  const resOptions = useMemo(
    () => (allowedRes?.length ? allowedRes : [...VIDEO_RESOLUTIONS]),
    [allowedRes],
  )
  const ratioOptions = useMemo(
    () => (allowedRatios?.length ? allowedRatios : [...VIDEO_RATIOS]),
    [allowedRatios],
  )

  /**
   * 时长的允许范围与常用档位 —— 与清晰度 / 比例同一套「以模型能力为准」的口径。
   *
   * `duration_min / duration_max` 由后端**逐模型**下发（Wan 3.0 是 2-30s、
   * Seedance 系是 4-15s）。后端没说的旧模型回落 2-30 —— 这个兜底只在前端
   * 拿到模型清单之前的一瞬生效（真选得到的视频模型都带这两个值）。
   *
   * `duration_options` 非空时以它为准（冲量版只认 5/10/15，此时区间里放 8s
   * 这种它不收的值就是给用户挖坑），档位也直接用那几档。
   */
  const durMin = modelInfo?.capabilities?.duration_min ?? 2
  const durMax = modelInfo?.capabilities?.duration_max ?? 30
  /**
   * 非空 = 该模型**只认这几档**（Google Omni 只有 6/8/10、冲量版只有 5/10/15）。
   *
   * 提成一个变量而不是在 `durPresets` 里就地取：下面那个归一 effect 也要用它，
   * 两处各读一次 `modelInfo?.capabilities?.duration_options` 的话，
   * 以后改判据（比如补一层「后端没说时按区间」）必然只改一处。
   */
  const durFixed = modelInfo?.capabilities?.duration_options
  const durPresets = useMemo(() => {
    const list = durFixed?.length
      ? durFixed
      : VIDEO_DURATIONS.filter((n) => n >= durMin && n <= durMax)
    // 兜底：万一档位一个都落不进范围（改了范围没改档位），至少给出上下限两颗
    const safe = list.length ? list : Array.from(new Set([durMin, durMax]))
    return safe.map((n) => ({ value: n, label: `${n}s` }))
  }, [durFixed, durMin, durMax])

  /**
   * 把清晰度 / 比例**校正在模型允许值内**。
   *
   * 为什么会跑偏：草稿里存着上一轮的值，用户换了模型（三档齐全的 Wan 3.0
   * → 只有 768P 的 MiniMax-H3）之后旧值就不合法了。不在这里校正的话，
   * 界面会停在一个没有对应按钮的档位上，一直拖到提交才由上游报错。
   *
   * 优先级：模型支持默认档（720P，见 `DEFAULT_VIDEO_RESOLUTION`）就用它，
   * 否则取模型给的第一个 —— catalog 里的顺序是按档位从高到低排的，
   * 第一个即该模型能给的最高档。
   *
   * ⚠️ **比例在 2026-10-09 改了口径**：用户口径「能自由填比例」，`RatioInput`
   * 旁边常驻一个输入框，任何「宽:高」形状的值都算合法。所以这里只修正
   * **不是比例形状**的值（空串、旧草稿里的脏值），**不再**把「不在模型清单里」
   * 当成要改的理由 —— 那样会把用户刚填的 `21:9` 立刻弹回 9:16，
   * 正是用户说的「不听话」。模型认不认这个比例交给上游判（用户已确认要这个自由）。
   */
  useEffect(() => {
    if (resOptions.length && !resOptions.includes(form.resolution)) {
      patch({
        resolution: resOptions.includes(DEFAULT_VIDEO_RESOLUTION)
          ? DEFAULT_VIDEO_RESOLUTION
          : resOptions[0],
      })
    }
    if (!isRatioValue(form.aspectRatio)) {
      patch({ aspectRatio: ratioOptions.includes(meta.ratio) ? meta.ratio : ratioOptions[0] })
    }
    /*
     * 时长同理：草稿里存着上一轮的 15 秒，换到一个只收 4-15s 的模型上没问题，
     * 但换到 3-15s（快乐马）之外的更窄范围、或用户自己敲过一个超出范围的数时，
     * 界面会停在一个上游不收的秒数上，直到提交才报错。这里把它夹回范围。
     *
     * ⚠️ **只夹区间**，不吸附到区间内的整数：用户自己敲的 7 秒是合法输入，
     * 在这里把它吸到最近的常用档位就成了「输入框不听话」（2026-10-09 的口径）。
     *
     * 但**固定档位模型另说**（`duration_options` 非空，如 Google Omni 的 6/8/10、
     * 冲量版的 5/10/15）：那种模型下**只有那几档是合法的**，区间夹取毫无意义
     * （6/8/10 的区间是 2-30，夹完 15 秒还是 15 秒）。这类模型在界面上由
     * `allowCustom=false` 挡住了「用户自己敲」，但**三条别的路仍然进得来**：
     * 旧草稿、`?model=` 深链、以及点了模板（15 秒的模板 + 只出 10 秒的引擎）。
     * 不收敛的话，用户看到的是「时长的档位一枚都不亮、按生成却报错」。
     *
     * 2026-10-10 加上这一支的直接原因就在第三条路上：默认引擎换成了只出 6/8/10 秒的
     * Google Omni，而模板里那几条 15 秒的（`vt-07` 等）会把自己的时长铺进来 ——
     * 它们各自带 `recommendedModel` 把引擎一并换掉，但**用户手动切回便宜引擎**时
     * 还要靠这里收敛。收敛到最近的合法档，不静默往回退（10 秒 ≠ 15 秒，
     * 但至少是一个上游真的会执行的组合）。
     */
    if (durFixed?.length) {
      if (!durFixed.includes(form.duration)) {
        patch({
          duration: durFixed.reduce(
            (a, b) => (Math.abs(b - form.duration) < Math.abs(a - form.duration) ? b : a),
            durFixed[0],
          ),
        })
      }
    } else if (form.duration < durMin || form.duration > durMax) {
      patch({ duration: Math.min(durMax, Math.max(durMin, form.duration)) })
    }
  }, [
    form.resolution,
    form.aspectRatio,
    form.duration,
    resOptions,
    ratioOptions,
    durFixed,
    durMin,
    durMax,
    meta.ratio,
    patch,
  ])

  /**
   * 换到**不收参考视频**的模型时，清掉已传的参考视频。
   *
   * 不清就是哑失败：界面上明明挂着一条视频，提交时上游直接忽略（它根本没这个入参），
   * 用户拿到一个「没参考」的成片，还以为模型不听话。
   *
   * 复刻档除外 —— 那一档参考视频是**必传输入**，换了个不收视频的模型应当由
   * `ready` 拦住不让提交（提示用户换个模型），而不是把用户传好的东西删掉。
   */
  useEffect(() => {
    if (meta.needVideo || refVideoMax > 0 || !form.refVideo.length) return
    patch({ refVideo: [] })
  }, [meta.needVideo, refVideoMax, form.refVideo.length, patch])

  /**
   * 落地对齐（只在挂载跑一次）。三件事，都是为了消灭「看着像坏了」的状态：
   *
   * ① **地址栏点名了另一档**（老书签 `/main-video`、模板卡片、分享链接进来）：
   *    按那一档的默认值**整组**铺一遍。只换场景不换时长就会出现
   *    「模式是商品视频、时长却是带货短视频的 15 秒」这种混搭，而且时长按秒计价，
   *    报价会跟着串档。
   * ② **场景不在本档清单里**：回落本档第一个场景。场景是 chips 控件，
   *    值不在列表里会一个都不高亮 —— 用户看到的是「场景没选」。
   * ③ **`?vtpl=` 点名了一个模板**（模板中心「短视频爆款」卡片进来的）：
   *    按模板铺参数。铺完**立刻把 vtpl 从地址栏摘掉** —— 它和 `mode` / `scene`
   *    不是一类参数：后两者是「用户当前状态」，刷新要还原；vtpl 是**一次性指令**，
   *    留下来就会与用户的后续改动打架（用户改完时长再刷新，会被模板按回去，
   *    改动静默丢失）。摘掉之后刷新走草稿，用户改的还在。
   */
  useEffect(() => {
    const m = getVideoMode(mode)
    const next: Partial<FormState> = {}
    if (sp.get('mode') && sp.get('mode') !== form.mode) Object.assign(next, defaultsOf(m.id))
    const wantScene = sp.get('scene')
    if (wantScene && m.scenes.includes(wantScene)) next.scene = wantScene
    else if (!m.scenes.includes(form.scene)) next.scene = m.scenes[0]

    const wantTpl = sp.get('vtpl')
    /* 🔴 **自存模板的深链不在这里消费** —— 它是异步拉的，挂载这一刻清单必然还是空的。
       按「找不到」处理就会把地址栏的 vtpl 删掉，用户从模板中心点自己的模板进来
       看到的是「点了没反应」，而且刷新也回不来（指令已经被自己吃掉了）。
       判据用 id 前缀（`vmt_` = 自存，见 db 的 `new_id("vmt_")`）：
       它同时是来源标记与「要不要等」的判据，不必再读一次 Context。 */
    if (wantTpl && !wantTpl.startsWith('vmt_')) {
      const t = templatesForMode(m.id).find((x) => x.id === wantTpl)
      if (t) Object.assign(next, t.preset)
      setSp(
        (prev) => {
          const p = new URLSearchParams(prev)
          p.delete('vtpl')
          return p
        },
        { replace: true },
      )
    }

    if (Object.keys(next).length) patch(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * `?vtpl=vmt_xxx` —— **自存模板**的深链（从模板中心「我的模板」点进来的）。
   *
   * 与上一条的 ③ 是同一件事，只是**不能同批做**：自存模板在 Context 里异步拉，
   * 挂载那一刻清单必然是空的。所以这里等 `tplReady` 再消费，消费完同样立刻
   * 把 `vtpl` 从地址栏摘掉（一次性指令的口径不变）。
   *
   * ⚠️ 判据是 `vmt_` 前缀而不是「清单里有没有」：后者在「用户手改了一个不存在的
   * id」时会永远等下去，地址栏里挂着一个消费不掉的参数。前缀判据的兜底是
   * 「等一次清单就摘掉」—— 最坏情况是这一跳没铺上参数，而不是卡住。
   */
  useEffect(() => {
    const want = sp.get('vtpl')
    if (!want || !want.startsWith('vmt_') || !tplReady) return
    const t = templatesForMode(mode, customTpls).find((x) => x.id === want)
    if (t) patch(t.preset)
    setSp(
      (prev) => {
        const p = new URLSearchParams(prev)
        p.delete('vtpl')
        return p
      },
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, tplReady, customTpls, mode])

  /**
   * 换档位 —— 等于换整条工作流：场景清单、时长、比例、默认平台一起换
   * （见 `defaultsOf` 的注释：这些参数是相互咬合的一套，只换一半就是混搭）。
   * 档位写进地址栏（可分享、可回退）并回到第一个页签。
   */
  const switchMode = (id: VideoModeId) => {
    /* 换档顺带清掉手改过的分镜提示词与镜头细化：换档 = 换整条片子（场景清单都不重叠），
       上一档的分镜词留着只会被当成这一档的输入 —— 那正是「参数与产出对不上」的来源。
       没改过时清空是空操作，不需要额外判断。
       ⚠️ 连 `sbInputKey` / `sbUsedUrl` 一起清：前者的判据是输入指纹、换档后本来就会不等
       （多余但不留脏值），后者指的是上一档那张图 —— 留着会让这一档新出的图被误判成「已用掉」。 */
    patch({ ...defaultsOf(id), storyboardPrompt: '', shotNotes: [], sbInputKey: '', sbUsedUrl: '' })
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('mode', id)
        // 上一档的场景参数对新档位无意义（两档场景清单不重叠），清掉免得残留
        next.delete('scene')
        return next
      },
      { replace: true },
    )
    setStep(0)
  }

  /**
   * `?scene=` 深链直达某个场景（如「模特手持产品」）。
   *
   * 侧栏已压成一层入口（2026-09-28），不再有场景子项；这条深链仍然保留 ——
   * 首页快捷入口、模板卡片、外部分享都可能带过来。
   *
   * URL 与表单双向一致曾经靠 `pickScene()` 写回 URL；2026-10-09 复刻档那排
   * 「复刻方式」chip 撤掉之后，页面里已经**没有任何场景选择器**了
   * （合并页的视频场景 chip 更早就撤了），所以只剩「URL → 表单」这一向。
   * 复刻档的 `scene` 固定为 `默认值（结构复刻）`，不再由 URL 改写 ——
   * `meta.scenes` 那一档里放的是复刻方式，不是画面场景。
   */
  const wantScene = sp.get('scene')
  useEffect(() => {
    if (isReplicate) return
    if (!wantScene || !meta.scenes.includes(wantScene)) return
    if (wantScene !== form.scene) patch({ scene: wantScene })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantScene, mode, isReplicate])

  /**
   * 当前命中的模板 —— **从表单反推，不是另存一个 state**。
   *
   * 存 `form.template` 的写法会让「用户改完风格、卡片还亮着」成为一种常态化撒谎；
   * 派生则天然自洽：改了任一字段就自动不再命中。切换档位同理 —— 两档参数不同，
   * 选中态自己就没了，不需要额外的清理分支。
   */
  const tpl = activeTemplate(mode, form, customTpls)

  /**
   * 分镜参考图的任务与产出（最后一个页签）。
   *
   * 它和视频任务是**同一条时间线上的两种 job**：`sourcePage` 都是本页，
   * 靠 `appSlug` 分开（`storyboard` vs 档位 slug）。所以右栏「结果」不会把它
   * 混进来（那边按 slug 集合收窄，见 `jobsFor(pageKey, slugs)`）——
   * 这是刻意的：右栏那栏的标题写着「<档位> 结果」，分镜图是**它的输入**，
   * 混进去会让「本轮生成了几支片子」这个数字变成假的。
   *
   * 取「最近一条」而不是「第一条」：用户重出分镜图时旧的那张还留在列表里，
   * 不排序的话可能一直显示第一次那张（用户会以为重出没生效）。
   */
  const sbJobs = useMemo(
    () => jobsFor(pageKey, STORYBOARD_SLUG).slice().sort((a, b) => b.createdAt - a.createdAt),
    [jobsFor, pageKey],
  )
  const sbRunning = sbJobs.find((j) => j.state === 'queued' || j.state === 'running') ?? null
  const sbDone = sbJobs.find((j) => j.state === 'succeeded' && j.outputs.length) ?? null
  const sbFailed = !sbRunning && !sbDone ? (sbJobs.find((j) => j.state === 'failed') ?? null) : null

  /**
   * 分镜提示词 —— **从当前输入派生 + 允许手改，但手改只在输入没变时算数**。
   *
   * 用户原话是「应该生成提示词，然后生成分镜图，你有思考吗？」——
   * 分镜这一步要的是两个可见的动作：先有提示词、再拿它去出图。
   * 提示词由 `buildStoryboardPrompt` 从**「内容与场景」页 + 「输出参数」页**派生，
   * 用户在这一屏可以直接改。
   *
   * ⚠️ 这违反了「提示词只在提交确认弹窗里展示」那条既有做法（见 `StudioPromptList`
   * 的注释），是**有意为之的一处例外**：别的页的提示词是「发出去才知道长什么样」，
   * 而分镜提示词是**生成前必须先定稿的输入**（用户要边看边调），
   * 放到「提交前那一步」就等于不给改的机会。视频提示词仍然只在那两处出现。
   *
   * ## 手改版与镜头细化**绑定输入指纹**（2026-10-05 修，用户两次报障）
   *
   * 以前是 `sbPrompt = 手改版 || 自动版` —— 手改版一挂上去就**永久压住**自动版，
   * 换产品 / 换模板 / 改时长它都不动。用户先问「为什么这个不会跟着之前的产品的
   * 信息改变的？」，第二次直接给截图：选了「卡点换装」模板、分镜图还是上一个产品的水壶。
   *
   * 现在按「这份东西是为哪组输入写的」判断（`storyboardInputKey`）：
   * 输入一变，手改版与镜头细化**同时失效**，自动版接管。
   * 「同时」是刻意的 —— 只让其中一个失效会出现「提示词是新版的、镜头描述还是
   * 上一个产品的」这种半新半旧状态，比全旧更难判断。
   */
  /**
   * 分镜出图**实际上送**的参考图：产品图在前、场景图在后，按模型额度截断。
   *
   * ⚠️ 这是「产品图 / 场景图现在喂分镜出图」这件事的**单一真源**：
   * 提示词里【参考图用法】写的张数（`sbAutoPrompt`）与实际发出去的那一批
   * （`doStoryboard` 的 `imageUrls`）都从它派生 —— 两处各算一次的话，
   * 额度一截断就会出现「词里写着前 2 张是产品图、实际只发了 1 张」，
   * 模型照着不存在的图去还原（本项目反复踩过的那条）。
   *
   * 与「视频只发分镜图」那条一样按 `refImageMax` 截：场景图排在产品图之后，
   * 额度不够时**先丢场景图**（产品外观丢掉 = 九格里的货不对板）。
   */
  const sbRefs = useMemo(() => {
    const products = form.refs.slice(0, refImageMax)
    const scenes = form.sceneRefs.slice(0, Math.max(0, refImageMax - products.length))
    return { urls: [...products, ...scenes], products: products.length, scenes: scenes.length }
  }, [form.refs, form.sceneRefs, refImageMax])

  const sbInputKeyNow = useMemo(
    () =>
      storyboardInputKey({
        ...form,
        storyboard: tpl?.storyboard,
        /* 产品图 / 场景图现在进分镜提示词（见 `sbAutoPrompt`），所以它们也算输入 */
        refProductCount: sbRefs.products,
        refSceneCount: sbRefs.scenes,
      }),
    [form, tpl, sbRefs],
  )
  /** 手改版/镜头细化是否仍属于当前这组输入。指纹为空 = 从没手改过，一律算不绑定 */
  const sbBound = !!form.sbInputKey && form.sbInputKey === sbInputKeyNow
  const sbCustomPrompt = sbBound ? form.storyboardPrompt.trim() : ''
  const sbNotes = sbBound ? form.shotNotes : EMPTY_NOTES
  /** 手改/细化过、但已因输入变化而失效 —— 界面必须说清，不能悄悄换掉用户写的东西 */
  const sbEditExpired = !sbBound && (!!form.storyboardPrompt.trim() || form.shotNotes.length > 0)
  const sbAutoPrompt = useMemo(
    () =>
      buildStoryboardPrompt({
        ...form,
        storyboard: tpl?.storyboard,
        shotNotes: sbNotes,
        /* 2026-10-09「出分镜时带产品图」：产品图 / 场景图作为参考图进分镜提示词，
           好让九宫格里的产品就是真产品 —— 生成视频只认这张分镜图，分镜不像，
           成片里的产品就不像。张数取 `sbRefs`（与实际发出去的那一批同源）。 */
        refProductCount: sbRefs.products,
        refSceneCount: sbRefs.scenes,
      }),
    [form, tpl, sbNotes, sbRefs],
  )
  const sbPrompt = sbCustomPrompt || sbAutoPrompt

  /**
   * 手上那张九宫格是不是**按当前这版提示词**出的。
   *
   * 为什么要这条判定（2026-09-30 用户报障）：取数只按「本页 + storyboard」，
   * 不看输入 —— 于是**上一轮为别的产品跑的分镜图会一直挂在页面上**，
   * 而且被当成「已作为参考图带上」跟着下一次视频一起提交。
   * 用户原话「怎么一直在的？」，截图里那张九宫格是上一个产品（榨汁杯）的，
   * 与当前表单毫无关系。真发生出去，成片就会照着别人的产品构图。
   *
   * 判据取**提示词**（`samePrompt`），不取事件时间：
   * 用户改完参数再改回来，那张图仍然有效 —— 拿时间戳比会误判成过期，
   * 反而每次都多花一次生图的钱。
   */
  const sbDoneUrl = sbDone?.outputs.find((o) => o.type === 'image')?.url ?? ''
  /**
   * 已经**随「生成视频」用掉**的那张 —— 用过就清空（2026-10-05 用户要求）。
   *
   * 判据取 URL：重新生成分镜图会得到新 URL，新图自动恢复可用，不需要额外重置。
   * 与 `sbFresh` 分开算是为了让两种「不算数」在界面上说不同的话 ——
   * 「参数变了」要提示重出，「用掉了」要说清它去哪了。
   */
  const sbUsed = !!sbDoneUrl && form.sbUsedUrl === sbDoneUrl
  const sbFresh = !!sbDoneUrl && !sbUsed && samePrompt(sbDone?.prompt, sbPrompt)
  /** 当前提示词下可用的分镜图（只有它会被带上、才会渲染成大预览） */
  const sbUrl = sbFresh ? sbDoneUrl : ''
  /** 上一版提示词留下的那张 —— 只在页面底部提一句，不占预览位、不进参考图 */
  const sbStaleUrl = sbFresh || sbUsed ? '' : sbDoneUrl

  /**
   * 分镜提示词折叠区的展开态。
   *
   * 为什么存成 state 而不是交给浏览器：折叠头走 `CardHead`（全站唯一一份折叠实现，
   * `open` 受控），它要求调用方持有状态。默认**收起** —— 出图之后用户看的是图，
   * 提示词属于「要改才点开」的东西。
   */
  const [sbPromptOpen, setSbPromptOpen] = useState(false)

  /** 「AI 细化镜头」进行中（按钮转圈 + 防连点） */
  const [sbShotsBusy, setSbShotsBusy] = useState(false)

  /**
   * 让 AI **只写镜头那一层**，锁区由模板原样拼回（2026-10-04）。
   *
   * 这颗按钮的上一版叫「AI 写一版」，做的是**整份重写提示词**（`api.refinePrompt`）。
   * 它的结构性问题在于：锁区（3×3 等分 / 镜号 1-9 / 九格白缝 / 技术要求）与自由区
   * 混在同一个字符串里，模型重写一遍，那些硬约束就全靠 system 里「保留原有结构与
   * 硬约束」这句**求**它别改。求是概率，不是保证 —— 改写过的提示词看着仍然像那么
   * 回事，改坏了对着截图看不出来。
   *
   * 现在改成按层分工：
   * · **上下文**是「渲染好的骨架 + 一段要你做的」（`buildBoardShotsFillBrief`，
   *   骨架与真正要发出去的那份同源）—— 模型看到「其余都定稿了、就差这几段画面描述」，
   *   比看到一堆要求更知道该写什么；
   * · **返回**只有每段的画面描述（`shots`），锁区由模板拼回，全程不经过模型的手。
   *
   * 段数必须对得上（见下面 `expected`）：换模板、改时长都会变镜头数，错位贴上去
   * 比不贴更糟，所以**对不上一律整体不覆盖**，并把实际段数如实报出来。
   * （2026-10-04 真调实测：不把「数条数不是数格数」说到上下文里，模型会按**格数**
   * 返回 —— 3 段写成 9 项。所以那句提醒同时写在上下文与 system 两处。）
   *
   * 另外三件事与原来一致：
   *
   * ① **它不是一次生成任务**。不建 job、不进任务中心、右栏不会多一张卡 ——
   *    它改的是**输入**，产物就是输入框里那段文字。但它真花钱（后端按条落用量账本），
   *    所以失败要如实报、成功要报花了多少。
   * ② **结果写进 `form.shotNotes`**（镜头层）并顺手清掉 `storyboardPrompt` ——
   *    `sbPrompt = 手改版 || 自动版`，手改版非空时细化出来的镜头根本看不到。
   *    用户点这颗按钮要的就是 AI 这版，所以回到自动版（手改被清在 toast 里说明）。
   * ③ **细化后手上那张九宫格会失效**（`sbFresh` 按提示词逐字比对）。不是 bug ——
   *    提示词变了，那张图就不是这支片子的镜头了。toast 里必须说明，否则用户以为图丢了。
   */
  const doAiShots = async () => {
    if (sbShotsBusy) return
    /* 当前分镜的段数。AI 必须给出同样多的段 —— 不符就整体不覆盖 */
    const expected = videoStoryboard(form.duration, tpl?.storyboard).length
    setSbShotsBusy(true)
    try {
      const res = await api.fillForm({
        kind: 'board-shots',
        /* 骨架用**绑定后**的镜头描述（`sbNotes`）：过期的细化不能让模型照着它再写一版，
           否则「上一组参数的镜头」会借这次细化重新落进新提示词里。 */
        brief: buildBoardShotsFillBrief({ ...form, storyboard: tpl?.storyboard, shotNotes: sbNotes }),
        sourcePage: pageKey,
      })
      /*
       * 拿到响应 = 后端**已经扣过额度**（`/form/fill` 是**按次计费**的：
       * `_bill_text_call` 在解析字段之前就收了钱）。所以刷新放在这里，
       * 而不是放在下面的成功分支 —— 段数对不上时前端会抛错、整体不覆盖，
       * 但那笔钱照样花了，余额必须动，否则用户以为白报了个错。
       */
      void refreshBalance()
      const lines = (res.fields.shots ?? '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
      if (lines.length !== expected) {
        throw new Error(
          `模型返回了 ${lines.length} 段镜头描述，与当前分镜的 ${expected} 段对不上，` +
            `这次没有覆盖你已有的内容；可以再点一次。`,
        )
      }
      const hadCustom = !!sbCustomPrompt
      /* 细化结果**当场绑定当前输入指纹** —— 与手改版同一套规矩：输入一变就失效。
         不写这一格的话，这批镜头描述会在换了产品之后继续生效（见 `sbInputKey` 的注释）。 */
      patch({ shotNotes: lines, storyboardPrompt: '', sbInputKey: sbInputKeyNow })
      toast.success(
        `AI 已细化 ${lines.length} 段镜头（${res.model} · ¥${res.cost.toFixed(2)}` +
          (res.coins > 0 ? ` · 扣 ${res.coins} 积分` : '') +
          ` · ${(res.elapsedMs / 1000).toFixed(1)}s）。` +
          (hadCustom ? '你手改过的那份提示词已清掉，现在按参数自动生成。' : '') +
          `${sbFresh ? '原来那张九宫格已按新提示词失效，重新出一张即可。' : ''}`,
      )
    } catch (e) {
      // 如实报错，且把后端/上游的原话带出来：Key 失效、限流、返回段数不符
      // 三种原因的处理方式完全不同，吞成「失败了」等于让人去猜。
      toast.error(`AI 细化镜头失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSbShotsBusy(false)
    }
  }

  /**
   * **实际上送**的参考图清单：**只有那张九宫格分镜图**。
   *
   * 2026-10-09 用户口径「参考图应该是分镜，而不是产品」：生成视频时
   * 产品图 / 场景图**不再上送**（它们改去喂分镜出图，见 `doStoryboard`），
   * 分镜图成了模型唯一的视觉参考。
   *
   * 为什么仍要「先算这一份、再让提示词跟它走」，而不是直接塞 `[sbUrl]`：
   * 上游对 `input_images` 是**硬截断**（多出来的直接丢，不报错），而额度由模型
   * 能力下发（`refImageMax`，个别模型是 0）。**发什么、词里就说什么** ——
   * 两处都从这一份派生，才不会出现「词里写着有一张分镜参考图、图却被截没了」。
   *
   * 取的是 `sbUrl` —— **只有「按当前提示词出的那张」才在清单里**（见 `sbFresh`）。
   * 上一版提示词留下的那张不算数：把它带上，等于让成片照着别的产品 /
   * 别的时长排的镜头去拍。额度为 0 时 `sent` 为空 —— 那一档不认任何参考图，
   * 由 `doGenerate` 的拦截说明这一点（见那里的 `blocked`）。
   */
  const refPlan = useMemo(() => {
    const sent = (sbUrl ? [sbUrl] : []).slice(0, refImageMax)
    return {
      sent,
      /** 分镜图是否真的进了本次上送清单（没进就在分镜页说明，别让用户以为带上了） */
      storyboard: sent.length > 0,
    }
  }, [sbUrl, refImageMax])

  /**
   * 缺分镜图时「生成视频」要说的话（**有值 = 确认弹窗里拦住、放行不了**）。
   *
   * 用户口径「这个分镜图需要强制生成才行」，且三选一里选了「可点，弹窗里拦住」——
   * 所以右栏那颗按钮**仍然可点**（在那儿置灰会让人以为页面坏了），点开后由
   * 确认弹窗说清缺什么、去哪补、补完再来。这也与全站其它花钱入口同款：
   * 真正的关口只该有一处，就是提交前那一步。
   *
   * 三种「缺」分开说，**不能一律回一句「请出分镜图」**：
   * 「一次都没出过」与「出过但已经随上一支片子用掉了 / 参数改了失效了」，
   * 对用户的下一步动作完全不同 —— 后者若被指回去「先出分镜图」，
   * 他会以为是自己漏了、白花一次生图的钱。判据与分镜页那两条提示文案同源
   * （`sbUsed` / `sbStaleUrl`）。
   *
   * ⚠️ 额度为 0 的模型（个别模型完全不收参考图）**另说**：那不是「去出张图」能解决的，
   * 得换模型 —— 否则用户会陷在「出了图也不让生成」的死循环里。
   */
  const sbGateReason =
    refImageMax <= 0
      ? `当前模型（${modelInfo?.name ?? '所选模型'}）不接受参考图，而生成视频只认分镜图 —— 到「视频模板」换一个支持参考图的模型再生成`
      : refPlan.storyboard
        ? undefined
        : sbUsed
          ? `上一张分镜图已经随之前的片子用掉了，请到「${paneTitle('storyboard')}」页按当前参数重出一张再生成`
          : sbStaleUrl
            ? `手上的分镜图是按改动前的参数出的、已经不算数，请到「${paneTitle('storyboard')}」页重出一张再生成`
            : `请先到「${paneTitle('storyboard')}」页出一张九宫格分镜图 —— 生成视频只以它为参考图`

  /**
   * 当前挂着的参考视频（**公网绝对地址**）。空串 = 还没传。
   *
   * 只取**第一条**：参考视频的用途是「复刻它的镜头怎么走」，两条片子给出的是
   * 两套互相冲突的镜头结构，模型只能瞎折中。所以分析也只针对这一条。
   *
   * **上传后拿到的就是腾讯云 COS 的公网直链** —— `/api/aigc/upload` 对视频走
   * `cos.upload_bytes`，回来的 `downloadUrl` 就是它（图片才走图床链）。
   * 所以这里**不做任何拼装**：上游 apilio 要能从公网把这条片子拉过去读，
   * 而这个地址本来就是公网可达的。
   *
   * ⚠️ 但**别想当然**：`storage.save_bytes` 那条链路（图片走的那条）回的是站点
   * 相对地址 `/static/...` —— 本机跑的时候 Vite 代理能取到，**公网取不到**。
   * 所以分析卡对「不是 http(s) 开头」的地址会给出明确提示，
   * 而不是发出去干等超时（那种症状看着像「后端卡死」）。
   */
  const analysisVideoUrl = (form.refVideo[0] ?? '').trim()
  /**
   * 真正会进提示词的那份镜头结构 —— **失效判定在提示词这条路上也要做一遍**。
   *
   * 卡片里那个「换片了，请重新分析」只是**给人看的**；提示词这条路才是花钱的那条。
   * 两处共用 `liveVideoAnalysis` 一份实现，所以不会出现「卡片说过期了、提示词却照用」
   * 这种一半对一半错的状态。
   *
   * 定义在这里（而不是在下面 JSX 附近）是**必须的**：`prompt` 的 useMemo 在本次
   * 渲染中就会执行，`const` 在那之前还处于 TDZ，放后面会直接 ReferenceError。
   */
  const analysisForPrompt = useMemo(
    () => liveVideoAnalysis(form.videoAnalysis, analysisVideoUrl),
    [form.videoAnalysis, analysisVideoUrl],
  )

  /**
   * 提示词。模板的**镜头结构约束**追加进【补充】段（`extra` 在原字段里本来就透传），
   * 模板的**三段式分镜**走 `storyboard` 参数（进【镜头脚本】段，右栏卡片共用同一份）；
   * 参考图的角色（几张产品图、几张场景图、有没有分镜图）也一并告诉提示词 ——
   * 不说明的话，模型会把场景图里的桌面当成要复刻的场景、把产品图里的人当成产品，
   * 更糟的是把九宫格分镜图**连格线一起复刻**（成片变成一格一格的拼贴）。
   *
   * 不在提示词里新开变量：`config/prompts.ts` 与它的 smoke 断言都不用动结构。
   */
  const prompt = useMemo(
    () =>
      buildVideoPrompt({
        ...form,
        /* ⚠️ 必须在 `...form` **之后**覆盖：`form.shotNotes` 可能是上个产品的细化描述，
           而这条提示词与分镜图提示词共用同一份镜头数据（见 `effectiveStoryboard`）。
           漏了这一行就会出现「分镜图跟着产品变了、成片提示词还在讲上一个产品」。 */
        shotNotes: sbNotes,
        storyboard: tpl?.storyboard,
        /* 复刻档的 `scene` 存的是**复刻方式**（结构复刻 / 节奏复刻），不是画面场景 ——
           提示词里那一行的前缀词跟着档走，否则模型会去找一个叫「结构复刻」的场景。
           `scene` 字段本身两个档共用一个（它的值要提交、要进草稿、要落库）。 */
        sceneLabel: isReplicate ? '复刻方式' : undefined,
        refStoryboard: refPlan.storyboard,
        /* ⚠️ 参考视频**必须进提示词**，不能只当素材发出去。
           只发素材的话，模型手里有视频、【镜头脚本】却在讲通用三段式
           （`GENERIC_BEATS`），成片就是套模板、跟参考视频没关系。
           复刻档（`/video-replicate`）之所以「必传参考视频」，正是靠这一行才算数。 */
        refVideoCount: form.refVideo.length,
        /* ⚠️ 分析结果只传**没过期的**那一份 —— `analysisForPrompt` 已经替提示词
           做过失效判定（与卡片同一条 `liveVideoAnalysis`）。直接把 `form.videoAnalysis`
           塞进去的话，换了参考视频却没重新分析时，成片会按**上一条片子**的镜头拍 ——
           而界面上的卡片多半显示正常，只有钱花了才发现。 */
        videoAnalysis: analysisForPrompt,
        extra: [form.extra, templateShotLine(tpl)].filter(Boolean).join('\n'),
      }),
    [form, tpl, refPlan.storyboard, sbNotes, analysisForPrompt],
  )

  /**
   * 点模板 = 整组铺参数（+ 推荐引擎）。地址栏不动 —— 模板是派生量，
   * 刷新后表单原样回来，选中态自然还在。
   *
   * ## 为什么现在连引擎一起铺（2026-10-10）
   *
   * 页面默认引擎是便宜的 Google Omni（¥0.15/秒），但它**只出 6/8/10 秒、
   * 只有 16:9 / 9:16**。模板里那几条 15 秒的（`vt-07/08/09/13`）与方版 1:1 的
   * （`mv-02/vt-15`）压在它下面会立刻被时长控件收敛回 10 秒 ——
   * 而 `activeTemplate()` 是拿 `preset` 逐字段比出来的，时长一改，**卡片当场脱钩**。
   * 用户看到的是「点了没反应」：卡没亮、参数还被改了一半。
   *
   * 所以 `recommendedModel` 是**能力约束的载体**，不是「我们觉得这条该配谁」的偏好：
   * 它保证「铺开的 preset」与「当前引擎」互相跑得通。留空（自存模板）就不动引擎 ——
   * 那条的参数是用户自己那套，我们不知道该配谁，不该替他换通道。
   *
   * ⚠️ 它**不进选中态判据**：`activeTemplate()` 仍旧只比 preset 那四个字段
   * （见 `config/videoTemplates.ts` 硬约束 2）。把引擎加进去的话，用户点完模板
   * 再换个引擎，卡片就会从「选中」翻成「没选中」—— 而他明明还在用这条模板。
   */
  const pickTemplate = (t: AnyVideoTemplate) =>
    patch(t.recommendedModel ? { ...t.preset, model: t.recommendedModel } : t.preset)

  /**
   * 正在预览的模板（点卡片上那颗播放按钮进来的）。`null` = 抽屉关着。
   *
   * **预览不铺参数**：它只是让人先看清楚「这条片子会怎么拍」，
   * 点抽屉里的「用这个模板」才真的 `pickTemplate`。两件事分开，
   * 用户翻着看几条也不会把表单搅乱。
   */
  const [previewTpl, setPreviewTpl] = useState<AnyVideoTemplate | null>(null)

  /**
   * 分镜脚本 —— 与提示词的【镜头脚本】是**同一份数据**（`videoStoryboard` 单一真源）。
   * 选中模板时用模板自带的 beats，没选模板退回通用三行。
   */
  /**
   * 分镜脚本 —— 与提示词的【镜头脚本】是**同一份数据**（`effectiveStoryboard` 单一真源）。
   * 选中模板时用模板自带的 beats，没选模板退回通用三行。
   *
   * ⚠️ 以前这里调的是 `videoStoryboard`（只吃模板），而提示词走 `effectiveStoryboard`
   * （还吃镜头细化）—— 于是用户点了「AI 细化镜头」之后，**右栏这张卡与提示词里的
   * 【镜头脚本】是两份不一样的东西**，而卡片下面还写着「与提示词里的【镜头脚本】
   * 是同一份数据」。现在两边都从 `effectiveStoryboard` 出，且共用绑定后的 `sbNotes`。
   */
  const storyboard = useMemo(
    () => effectiveStoryboard(form.duration, tpl?.storyboard, sbNotes),
    [form.duration, tpl, sbNotes],
  )

  /* ---------------- 「AI 生成提示词」（现在挂在确认弹窗里那颗按钮上） ---------------- */

  /**
   * 2026-10-09：这一步**不再有 state**。原先 AI 出的那版存在右栏「成片提示词」卡里、
   * 与提交无关；现在按钮进了确认弹窗，出的词**直接写进弹窗那个可编辑框**
   * （= 将发送的那一份），所以页面侧只留下面这个纯函数 `doAiPrompt` + 它的前置判据。
   */

  /**
   * 「AI 生成提示词」的硬前置 —— **分档**。
   *
   * 合并页：产品名是唯一的硬前置（上下文里没有产品，AI 只能编一个，同 `/banner`）。
   * 复刻档（2026-10-09 起）：产品名已不再单列（见 `stepBrief`），唯一的内容输入
   * 就是「产品与要求」那一格 —— 所以就以它为准。判据写在这里而不是两处各写一遍，
   * 否则按钮的禁用态与它的 title 迟早说两件不同的事。
   */
  const aiPromptBlocked = isReplicate ? !form.sellingPoints.trim() : !form.productName.trim()
  const aiPromptBlockReason = isReplicate
    ? `先在「${paneTitle('brief')}」里写清这条片子要讲什么 —— 没有它，AI 只能编一个产品`
    : '先填产品名称 —— 没有它，AI 只能编一个产品'

  /**
   * 把前面几页的参数汇总成一版成片提示词（`POST /api/aigc/prompt/refine`，
   * `kind='video-prompt'`）。**由确认弹窗里那颗「AI 生成提示词」调用**
   * （见 `doGenerate` 塞给 `info.promptAi` 的 `generate`）。
   *
   * 2026-10-09 改：原先它在右栏「成片提示词」卡头上、出的词**不参与提交**。
   * 用户口径「AI 成片提示词放到确认弹窗」之后，按钮进确认框，出的词直接填进
   * 那个可编辑框 —— 所以这里只**返回文本**、不写 state（写框由弹窗自己做）。
   *
   * 与 `doAiShots`（AI 细化镜头）仍是两件事，别合并：
   * · `doAiShots` 写的是**分镜图提示词**的镜头层，结果落进 `form.shotNotes`；
   * · 这一颗只产出一版文本，交给弹窗写进提交框。
   *
   * 入参与 `buildVideoPrompt` 那次调用**逐项对齐**（同一份 `sbNotes`、同一个
   * `analysisForPrompt`、同一份 extra 拼接）—— 两边参数不一致时，AI 汇总出来的
   * 东西会与真实提示词讲两件事，而用户手上只有一份能看。
   *
   * 返回 `null` = 没出结果（失败 / 空结果）；弹窗据此**不动**那个框（发起页已 toast）。
   */
  const doAiPrompt = async (): Promise<string | null> => {
    try {
      const res = await api.refinePrompt({
        kind: 'video-prompt',
        /* 空 = 让模型从 `brief` 从零汇总。**不把模板拼好的提示词传进去**：
           传了就是「在它上面改写」，硬约束（零文字画面等）会过模型的手，
           改坏了对着截图看不出来 —— 这正是「AI 写一版」当初被撤掉的原因
           （见 `doAiShots` 的注释）。只给参数，让它自己组织一版。 */
        prompt: '',
        brief: buildVideoPromptRefineBrief({
          ...form,
          shotNotes: sbNotes,
          storyboard: tpl?.storyboard,
          sceneLabel: isReplicate ? '复刻方式' : undefined,
          refStoryboard: refPlan.storyboard,
          refVideoCount: form.refVideo.length,
          /* 与提示词同一条失效判定：过期的分析一个字都不许进去，
             否则 AI 会照着**上一条片子**的镜头汇总 */
          videoAnalysis: analysisForPrompt,
          extra: [form.extra, templateShotLine(tpl)].filter(Boolean).join('\n'),
        }),
        sourcePage: pageKey,
      })
      /* 拿到响应 = 后端已经扣过额度（`/prompt/refine` 与 `/form/fill` 同族，
         在解析之前就收钱）。所以刷新放在这里，而不是成功分支里。 */
      void refreshBalance()
      const text = res.prompt.trim()
      /* 空结果必须报错：「花了钱、提示成功、一个字没出现」是最坏的失败形态 */
      if (!text) throw new Error('模型没有返回任何内容，这次没有生成')
      toast.success(
        `AI 已按当前参数汇总出一版提示词（${res.model} · ¥${res.cost.toFixed(2)}` +
          (res.coins > 0 ? ` · 扣 ${res.coins} 积分` : '') +
          ` · ${(res.elapsedMs / 1000).toFixed(1)}s）。已填进确认框，可继续改。`,
      )
      return text
    } catch (e) {
      // 如实报错并带出后端原话：Key 失效 / 限流 / 超时三种原因处理方式不同
      toast.error(`AI 生成提示词失败：${e instanceof Error ? e.message : String(e)}`)
      return null
    }
  }

  const platform = getPlatform(form.platform)

  const price = usePricePreview({
    // app_slug 逐档保留（后端 registry / taxonomy / 报价都认这三个值）——
    // 合并的是页面，不是计价口径
    appSlug: meta.appSlug,
    model: form.model,
    outputs: 1,
    // ⚠️ 必须是**表单里的真实清晰度**。这里以前写死 `'1080p'`，
    // 而贰春通道是按清晰度**分别计价**的（Wan 3.0 的 480P/720P/1080P
    // 分别是 ¥0.158/0.4/0.8 每秒），写死等于给所有档位报同一个价 ——
    // 用户看到的预估与最终扣费差最多 5 倍。
    resolution: form.resolution,
    aspectRatio: form.aspectRatio,
    inputs: { duration: form.duration },
  })

  /**
   * 本页的任务与产出。
   *
   * ⚠️ 2026-10-05 并档后**不能再按 `meta.appSlug` 收窄**：页面上只剩一档，
   * 但库里还躺着当年在「商品视频」档提交的记录（slug = `main-video`）。
   * 按当前档的 slug 过滤，用户切过来会以为历史记录被清空了 ——
   * 而这不是「正常收窄」，是真的少了一批他自己的东西。
   * 所以判据换成 `STUDIO_VIDEO_SLUGS`（合并页历来写过的全部 slug）。
   *
   * 仍要**排除 `storyboard`**：分镜参考图与本页视频任务是同一条时间线上的两种 job，
   * 它是视频的**输入**而不是产出，混进「本轮结果」会让「生成了几支片子」变成假数字
   * （见上面 `sbJobs` 那段）。集合里没有它，所以这里天然被挡住。
   *
   * 复刻档走自己那一支：它的 slug 就是 `video-replicate`，与合并页没有交集 ——
   * 两个页面共用一个组件，`pageKey` 已经把它们分开了，这里只是把 slug 也分开。
   */
  const slugs = isReplicate ? ['video-replicate'] : STUDIO_VIDEO_SLUGS
  const jobs = jobsFor(pageKey, slugs)
  const outputs = outputsFor(pageKey, slugs)
  const needVideo = meta.needVideo

  /**
   * 右栏「本页历史」的两个档（2026-10-09）。
   *
   * 分镜参考图的记录**以前在右栏里一条都看不到**：它按 `STORYBOARD_SLUG` 与成片
   * 分开取数（`sbJobs`，页内那张九宫格卡在用），而右栏只吃 `jobs`（`slugs` 集合里
   * **刻意不含 `storyboard`**）。那条「刻意」仍然成立 —— 它是视频任务的**输入**，
   * 混进「本轮结果」会让「生成了几支片子」变成假数字 —— 所以这里**沿用分档**：
   * `jobs` 一个字没动，只是把 `sbJobs` 一并交给右栏，由用户自己切着看。
   *
   * ⚠️ **只有合并页有分镜这一步**：复刻档没有「分镜参考图」页签（`tabs` 里没有它），
   * 所以那一档不渲染这个 chip —— 否则会出现一个永远空着的档。
   * 判据取 `tabs`（页签清单的真源），不写 `isReplicate`：页签数的沿革那条注释里
   * 反复强调过「判据写 `tabs.length - 1` 而不是 `PANES.length - 1`」同理。
   */
  const historyTabs = [
    { id: 'video', label: meta.name, jobs },
    ...(tabs.some((t) => t.id === 'storyboard')
      ? [{ id: 'storyboard', label: '分镜参考图', jobs: sbJobs }]
      : []),
  ]

  /* ----------------------- 存为模板（用户自存模板） ----------------------- */

  /** 「存为模板」弹窗开着没有。两个入口（模板条尾部 / 分镜页）共用这一个 state */
  const [saveTplOpen, setSaveTplOpen] = useState(false)
  /** 分镜九宫格的放大遮罩（用户口径：「可以点开」） */
  const [sbZoom, setSbZoom] = useState(false)

  /**
   * 存模板时一并记下的**成片样例** —— 模板卡上那颗播放键播的就是它
   * （用户口径「封面是分镜图，内容是生成的视频」）。
   *
   * 取法：本页**最近的、成功的、有视频产出、且比例与本模板一致**的任务里的第一条视频。
   * 不用 `outputs[0]` —— 那个顺序由 Context 决定，而这里要的是「最新」，
   * 靠 `createdAt` 显式排序，换一次 Context 的存储顺序也不会悄悄变成「最旧的」。
   *
   * 🔴 **`aspectRatio` 这一条过滤不能去掉**（2026-10-08 实测踩到）。
   *
   * 原先是「本页最近一条成功的片，不看参数」—— 那时我以为没问题，因为弹窗里
   * 写明了「带上本页**最近**这条成片」。但预览抽屉是**按 `preset.aspectRatio` 设视频框**的
   * （`TemplatePreview` 的 `.tprev__video`），于是「不过滤」的真实后果是：
   *
   * ```
   * 05:11 用 9:16 出了一条片（10s）
   * 06:47 把表单改成 1:1、出一张 1:1 的分镜图（没出片）
   * 06:49 点「存为模板」 → 抓的还是 05:11 那条 9:16
   * ⇒ 抽屉里写着「比例 1:1 / 时长 8s」，画的却是一个方框里夹一条竖片（两侧大片黑边），
   *    播放器读数 0:10。界面自己跟自己打架。
   * ```
   *
   * 这条与项目既有规则是同一件事 —— `verify-templates.py` 顶部那句
   * 「卡片写 1:1，播出来就得是方版，不能悄悄给一条竖版」。那条规则只管内置模板
   * （案例片是拿模板自己的提示词跑的，天然同比例），自存模板的示例片是**用户的历史产出**，
   * 得由这里保证。
   *
   * ⚠️ 只比 `aspectRatio`、不比 scene/style：后两项在画面上**没法程序判定**，
   * 硬比会写成一份谁也不信的等价判断；而比例是一眼看出来的那个，也是既有规则点名的那个。
   *
   * 取不到就留空：模板照存，卡片上的播放键回落到「逐格播分镜」
   * （见 `TemplatePreview` 的两级回落），不会因为「还没出过同比例的片」就不让存。
   * **宁可播分镜，也不要播一条比例不符的片** —— 后者会让用户以为这套参数出的是竖版。
   */
  const sampleVideoUrl = useMemo(() => {
    const latest = jobs
      .filter(
        (j) =>
          j.state === 'succeeded' &&
          /* 认不出比例的旧任务（老数据没有这列）一律不取：宁可没有示例片 */
          !!form.aspectRatio &&
          j.aspectRatio === form.aspectRatio &&
          j.outputs.some((o) => o.type === 'video' && o.url),
      )
      .sort((a, b) => b.createdAt - a.createdAt)[0]
    return latest?.outputs.find((o) => o.type === 'video' && o.url)?.url ?? ''
  }, [jobs, form.aspectRatio])

  /**
   * 打开保存弹窗。两个入口都走它（模板条尾部那张卡 + 分镜页那颗按钮）——
   * 「一次点击只对应一处实现」，两处各写一遍的话，
   * 迟早一处带封面、另一处忘带，症状是「从这儿存的模板卡片是灰的」。
   */
  const openSaveTemplate = () => {
    // 空表单存出来的模板是一套「出厂默认值」，对谁都没用 —— 先拦一道并说清原因。
    // 判据不是「有没有选模板」：手调出来的一整套参数同样值得存（用户口径正是
    // 「用户改完模板可以存储起来」），所以只要产品名或已命中模板任一成立就放行。
    if (!form.productName.trim() && !tpl) {
      toast.error('先填好产品名（或点一张模板），再存成自己的模板 —— 不然存下来的只是一套默认值')
      return
    }
    setSaveTplOpen(true)
  }

  /**
   * 弹窗里「分镜脚本」那块的那颗 **AI 扩写**（2026-10-08 用户口径
   * 「也可以用AI按钮扩写」）。
   *
   * 复用页面上那颗「AI 细化镜头」的同一个后端场景（`kind='board-shots'`）——
   * 它只让模型写**镜头那一层**，锁区（3×3 等分 / 镜号 / 技术要求）由模板原样拼回，
   * 所以扩写出来的东西不会把提示词的骨架改坏。这是刻意不另开一个场景的原因：
   * 同一件事（把每段画面写清楚）有两套 system，改一处必漏一处。
   *
   * ⚠️ **段数对不上一律整体不覆盖**（与 `doAiShots` 同一条）：弹窗里的镜头数
   * 就是这条模板的节奏，模型多给一段少给一段，贴上去比不贴更糟。
   *
   * ⚠️ 上下文里的镜头用**弹窗当前这一版**（`shots` 参数），不是页面上的 `storyboard`：
   * 用户可能刚改过两条再点这颗键，拿旧版去问的话，AI 会照着「改之前」重写，
   * 把他刚打的字冲掉 —— 而他点这颗键要的正是「在我这一版的基础上扩写」。
   */
  const aiExpandShots = async (shots: TemplateShotLine[]): Promise<TemplateShotLine[]> => {
    const res = await api.fillForm({
      kind: 'board-shots',
      brief: buildBoardShotsFillBrief({
        ...form,
        storyboard: tpl?.storyboard,
        shotNotes: shots.map((s) => s.d),
      }),
      sourcePage: pageKey,
    })
    /*
     * 拿到响应 = 后端**已经扣过额度**（`_bill_text_call` 在解析字段之前就收了钱）。
     * 所以刷新放在这里、不放成功分支 —— 下面段数对不上会抛错、整体不覆盖，
     * 但那笔钱照样花了，余额必须动，否则用户以为白报了个错。
     */
    void refreshBalance()
    const lines = (res.fields.shots ?? '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    if (lines.length !== shots.length) {
      throw new Error(
        `模型返回了 ${lines.length} 段镜头描述，与当前 ${shots.length} 段对不上，` +
          `这次没有覆盖你改过的内容；可以再点一次。`,
      )
    }
    toast.success(
      `AI 已扩写 ${lines.length} 段镜头（${res.model} · ¥${res.cost.toFixed(2)}` +
        (res.coins > 0 ? ` · 扣 ${res.coins} 积分` : '') +
        ` · ${(res.elapsedMs / 1000).toFixed(1)}s）。可以直接改，改完再点保存。`,
    )
    return shots.map((s, i) => ({ ...s, d: lines[i] }))
  }

  /**
   * 弹窗里「全片约束」那块的那颗 **AI 扩写**。
   *
   * 走的不是 `fillForm` 而是 `refinePrompt`（`kind='template-shot'`）：
   * 这个场景的产物是**一段正文**（不是结构化字段），而 `fillForm` 那一族
   * 是按 white-list 把键写回表单的 —— 硬塞进去只会多一层没人用的包装。
   * 后端的合法值清单在 `aigc._REFINE_SYSTEM`（唯一真源），这边只是取一个键。
   *
   * 传 `prompt` = 弹窗里当前那段（可能是用户手写的），让它在**这一版基础上**扩写；
   * 留空也能点 —— 后端 brief 里带着整支片子的参数，它会从零写一版。
   */
  const aiExpandShot = async (current: string): Promise<string> => {
    const res = await api.refinePrompt({
      prompt: current,
      brief: buildTemplateShotRefineBrief(
        { ...form, storyboard: tpl?.storyboard, shotNotes: sbNotes },
        form.productName.trim() || (tpl ? templateTitle(tpl) : undefined),
      ),
      kind: 'template-shot',
      sourcePage: pageKey,
    })
    void refreshBalance()
    /*
     * 后端 system 里已明令「绝对不能换行」，但那属于概率，不是保证 ——
     * 而这段文字会被拼进提示词的**一行**（`templateShotLine()`），
     * 多一个换行就把后面的段落结构顶乱。所以收口放在这里（返回给界面之前），
     * 与 `_strip_fence` 挡代码围栏是同一条思路：**不让坏格式走到下游去**。
     */
    const one = res.prompt.replace(/\s*\n+\s*/g, ' ').trim()
    if (!one) throw new Error('模型返回了空内容，这次没有覆盖你已有的约束；可以再点一次。')
    toast.success(
      `AI 已重写全片约束（${res.model} · ¥${res.cost.toFixed(2)}` +
        (res.coins > 0 ? ` · 扣 ${res.coins} 积分` : '') +
        ` · ${(res.elapsedMs / 1000).toFixed(1)}s）。可以直接改，改完再点保存。`,
    )
    return one
  }

  /**
   * 真正落库。**存的是结构，不是拼好的提示词**（用户选的口径）：
   * 五个参数 + 分镜脚本 + 全片约束 —— 下次套用时按**当时那个产品**重新拼词，
   * 所以这条模板换一个产品依然能用。把成品提示词存进去看着更省事，
   * 但那段文字里写着上一个产品的名字与卖点，换产品就废了。
   *
   * ⚠️ 分镜与全片约束取的是**弹窗里交回来的那一份**（`values.shots` / `values.shot`），
   * 不是页面上的 `storyboard` 与 `tpl.shot`：用户可能刚在弹窗里逐条改过
   * （2026-10-08 新加的能力），用页面那份等于把他的改动原地丢掉 ——
   * 而他看着弹窗里的字，绝不会想到「这些改动没生效」。
   *
   * 成功 / 失败的分工：**成功由这里 toast、失败由弹窗自己显示**。
   * 弹窗里再放一份「保存成功」是同一件事说两遍；而失败必须留在弹窗里 ——
   * 用户刚填的名字还在，改一个字就能重试，不用关掉重来一遍。
   * 所以这里**把异常原样抛回去**（吞掉它 = 用户点了保存、弹窗关了、什么都没发生）。
   */
  const saveTemplate = async (values: TemplateFormValues) => {
    /*
     * 失败**不在这里 catch**：异常原样抛回给弹窗，由它显示在框里。
     * 在这里吞掉（或顺手 toast 一条）都是错的 ——
     *   · 吞掉 = 用户点了保存、弹窗关上了、什么都没发生（最坏的一种"沉默失败"）；
     *   · 顺手 toast = 同一条错误同时出现在弹窗内和右上角，用户不知道该看哪。
     * 而弹窗里报错的价值在于：**刚填的名字还在**，改一个字就能重试。
     */
    await createVideoTpl({
      name: values.name,
      tag: values.tag,
      description: values.description,
      modes: [mode],
      preset: {
        scene: form.scene,
        style: form.style,
        duration: form.duration,
        aspectRatio: form.aspectRatio,
      },
      /* 分镜**按原形态回写**（`storyboardWithEditedShots`）：无条件存 `{shots}`
         会把三段式模板改写成多镜头，而形态是会进提示词的（量词「第 N 段」/「镜头 N」）
         ⇒ 刚点完保存，用户手里那张分镜图就被判成「不新鲜」。详见那个函数的注释。 */
      storyboard: storyboardWithEditedShots(tpl?.storyboard, values.shots),
      shot: values.shot,
      // 封面就是用户已经出好的那张 3×3 九宫格；后端会把它拉回来转存，避免上游直链过期
      coverUrl: sbUrl || '',
      sampleUrl: sampleVideoUrl,
    })
    toast.toast(
      `模板「${values.name}」已存到「我的模板」`,
      'success',
      // 带一颗「去看看」：存完最自然的下一步就是去模板中心确认它长什么样
      // （封面、播放键都在那边）。不带动作的话，用户得自己回想入口在哪。
      { label: '去模板中心', onClick: () => navigate('/') },
    )
  }
  /** 参考素材（产品图 / 场景图 / 参考视频）任一非空，就按图生视频提交 */
  const hasRef = needVideo
    ? form.refVideo.length > 0
    : form.refs.length > 0 || form.sceneRefs.length > 0
  const ready = needVideo ? form.refVideo.length > 0 : form.productName.trim().length > 0

  /**
   * 场景参考图还能传几张 —— **与产品图共享 `ref_image_max` 总额度**。
   *
   * 为什么不各自给满额：上游收的是一个 `input_images` 数组，
   * 两边各放 4 张的结果是提交 8 张、上游直接截断（用户以为自己传上了）。
   * 这里让「内容与场景」页的额度跟着素材页的已用量走，页面上写清剩余张数。
   */
  const sceneRefMax = Math.max(0, refImageMax - form.refs.length)

  /* ---------- 页签 · 分镜参考图（**只在合并页出现**；带动右栏提交栏） ---------- */

  /**
   * 分镜图的报价。
   *
   * `appSlug` 必须是 `storyboard` 而**不是当前档位**：后端按 slug 查产出类型
   * 与价目表，传档位 slug 会被当成视频、按秒报价（¥4.50 而不是 ¥0.10）。
   */
  const sbPrice = usePricePreview({
    appSlug: STORYBOARD_SLUG,
    model: STORYBOARD_MODEL,
    outputs: 1,
    aspectRatio: form.aspectRatio,
  })

  /**
   * 出分镜图。走**同一条报价确认流程**（`flow.request`）——
   * 它同样是一次真实扣费，不该因为便宜就绕过确认；复用同一条路径也意味着
   * 弹窗、扣费口径、失败落卡、右栏任务只有一份实现。
   *
   * ## 为什么要带产品图 / 场景图（2026-10-09 用户口径「出分镜时带产品图」）
   *
   * 上一版是**纯文生图**，理由是「把产品图发过去会让 9 个镜头退化成同一张
   * 产品定妆图的 9 个角度」。但那时的分工是「产品图直接喂成片」——
   * 自打「生成视频只发这张分镜图」之后（用户口径「参考图应该是分镜，而不是产品」），
   * 分镜图成了模型**唯一的视觉参考**：它里面的产品不像，成片里的产品就不像。
   * 所以产品外观必须在这里就还原，代价是接受「九格的产品是同一个」这件事。
   *
   * 发的顺序是产品图在前、场景图在后，与分镜提示词里【参考图用法】逐段写的
   * 张数**同源**（都从 `form.refs` / `form.sceneRefs` 算）；按 `refImageMax`
   * 截断 —— 截了图不改词，模型会去还原一张根本没发过去的图。
   */
  const doStoryboard = () => {
    /* 与提示词里的【参考图用法】**同源**（`sbRefs`）—— 别在这里再拼一次数组 */
    const refs = sbRefs.urls
    return flow.request({
      info: {
        title: '分镜参考图',
        appSlug: STORYBOARD_SLUG,
        model: STORYBOARD_MODEL,
        outputs: 1,
        aspectRatio: form.aspectRatio,
        refImages: refs.length,
        prompt: sbPrompt,
        price: sbPrice?.estimatedPrice ?? 0,
        basis: sbPrice?.basis,
        free: sbPrice?.isFreeThisCall,
        platform: platform.name,
      },
      args: {
        appSlug: STORYBOARD_SLUG,
        title: `分镜参考图 · ${form.productName || form.scene}`,
        sourcePage: pageKey,
        model: STORYBOARD_MODEL,
        prompt: sbPrompt,
        inputs: { ...form, __title: '分镜参考图' },
        imageUrls: refs,
        aspectRatio: form.aspectRatio,
        outputs: 1,
        estimatedMs: 45000,
      },
      // 辅助产物：成功后**不清**工作区（分镜表 / 产品信息还要用）—— 见 GeneratePayload.keepWorkspace
      keepWorkspace: true,
    })
  }

  /**
   * 当前提示词是不是还停在「模板通版」—— 确认弹窗据此给一条软提醒（`PriceConfirmInfo.promptHint`）。
   *
   * 判据只认**镜头有没有被细化过**（`sbNotes`）。模板自带的分镜是通版的
   * （「产品定格特写 / 使用过程实拍」这类通用动作，不含你的产品），只有走过
   * 「AI 细化镜头」之后每段画面才落到具体产品上 —— `sbNotes` 为空就等于这条提示词的
   * 【镜头脚本】还是模板原文，直接出片多半跟产品对不上。
   *
   * ⚠️ **还要排掉参考视频**（`form.refVideo` 非空）：复刻档的【镜头脚本】是从参考视频
   * 来的（`buildVideoPrompt` 里有参考视频时根本不输出模板分镜），那时提示词一点都不「通版」，
   * 再提醒「模板不够」就是误伤 —— 用户手里正拿着一条片子在复刻。
   *
   * ⚠️ 弹窗里手改 / 点「AI 生成提示词」不算在这一层：那两件事改的是**弹窗里那段文字**，
   * 由弹窗自己按「提示词有没有被改过」（`baseText === info.prompt`）收掉提醒。
   * 两处各管一段、不重复判断 —— 在这里也去比那段文字等于把判据写两遍。
   */
  const promptUntailored = sbNotes.length === 0 && form.refVideo.length === 0

  const doGenerate = () =>
    flow.request({
      info: {
        title: meta.name,
        appSlug: meta.appSlug,
        model: form.model,
        outputs: 1,
        aspectRatio: form.aspectRatio,
        resolution: form.resolution,
        /* 上送的参考图**只有那张分镜图**（见 `refPlan`）—— 弹窗那一行「参考图 N 张」
           说的就是它，别再算产品图 / 场景图 / 参考视频（那些现在不进 `imageUrls`）。 */
        refImages: refPlan.sent.length,
        /* 没有（按当前提示词出的）分镜图就不放行 —— 理由见 `sbGateReason` */
        blocked: sbGateReason,
        prompt,
        /* 模板通版的**软提醒**（只提醒、不挡）—— 见 `promptUntailored`。
           提醒里点名「AI 生成提示词」，是因为那句话只有展开下面的提示词才看得到。 */
        promptHint: promptUntailored
          ? '这条提示词还是模板通版，没有按你的产品细化 —— 建议展开下面的提示词、点「AI 生成提示词」再提交；否则镜头脚本可能跟产品对不上。'
          : undefined,
        price: price?.estimatedPrice ?? 0,
        basis: price?.basis,
        free: price?.isFreeThisCall,
        platform: platform.name,
        /* 「AI 生成提示词」—— 确认弹窗里那颗按钮（2026-10-09 从右栏「成片提示词」卡搬来）。
           `generate` 只返回文本，写框由弹窗自己做；`blocked` 是它点不动的原因
           （分档判据见 `aiPromptBlocked`，与合并页 / 复刻档各自的硬前置同一条）。 */
        promptAi: {
          generate: doAiPrompt,
          blocked: aiPromptBlocked ? aiPromptBlockReason : undefined,
          sourcePage: pageKey,
        },
      },
      args: {
        appSlug: meta.appSlug,
        title: `${meta.name} · ${form.productName || form.scene}`,
        sourcePage: pageKey,
        model: form.model,
        prompt,
        inputs: { ...form, __title: meta.name },
        /*
          ⚠️ 参考图与参考视频**分两个字段发**，不要拼在一起。

          以前这里写的是 `imageUrls: [...form.refs, ...form.refVideo]` ——
          参考视频被塞进图片数组，后端会按图片处理（贰春只认 media_id，
          会把它挂在 input_images 上，而上游看到的是一条视频素材）。
          拆开之后 `videoUrls` 一路走到适配器的 `input_videos`。

          2026-10-09 起 `imageUrls` **只有那张九宫格分镜图**（用户口径「参考图应该是
          分镜，而不是产品」）—— 产品图 / 场景图改去喂分镜出图（见 `doStoryboard`）。
          它仍从 `refPlan.sent` 取、不在这里现场拼：超额度 / 模型不收参考图时，
          提示词（`refPlan.storyboard`）与实际发出去的图必须同源，
          否则会各说各话（`refPlan` 就是为这件事存在的）。
        */
        imageUrls: refPlan.sent,
        videoUrls: form.refVideo,
        aspectRatio: form.aspectRatio,
        resolution: form.resolution,
        outputs: 1,
        estimatedMs: 240000,
      },
      /*
        分镜图**只在成片真的出片之后**才算「用掉」（2026-10-10 用户口径：
        「这个如果取消了，就不要强制再生成，这不符合人性」）。

        原来记在「点确认」那一刻（见 `PriceConfirmDialog` 的 `onConfirm`）—— 于是任务
        **失败 / 被用户取消**之后，那张分镜图照样作废：下一版弹窗拿红字把人拦回去
        「按当前参数重出一张再生成」，而同一个弹窗底下又写着「失败不扣费，可直接重试」，
        自相矛盾。取消就是 `state:'failed'` + `error.code:'ABORTED'`（见
        `JobCenterContext.cancel`），失败也一样没出片 —— 两种都没消耗掉这张图。

        改到这里（`onDone` 拿到本批任务**最终**的 state）之后：只有真出了片
        （`succeeded` / `partial`）才记「用掉」；失败 / 取消原样留着，直接重试即可。
        `sbUrl` 闭包取的是点确认那一刻的那张 —— 与弹窗展示、上送的都是同一张。
      */
      onDone: (jobs) => {
        if (sbUrl && jobs.some((j) => j.state === 'succeeded' || j.state === 'partial')) {
          patch({ sbUsedUrl: sbUrl })
        }
      },
    })

  /**
   * 「生成案例片」—— 从模板预览抽屉里点出来：自存模板还没有成片时，用**当前表单的
   * 产品与卖点**按这条模板的预设跑一条成片，完成后挂回模板（`sampleUrl`）。
   *
   * ## 为什么参数取模板的、产品取表单的
   *
   * 案例片的作用是回答「这条模板这么拍会得到什么」，所以时长 / 比例 / 场景 / 风格
   * 必须**逐项等于模板预设**（下面的 `buildVideoPrompt` 全从 `tpl.preset` /
   * `tpl.storyboard` 取，一个都不从表单拿）—— 否则卡片写着 8 秒 1:1、片子却是
   * 15 秒竖版，那正是 `fix_template_samples.py` 要防的「界面与片子对不上」。
   * 而【产品】【卖点】是模板里**没有**的东西（模板只存结构），只能从当前表单取。
   *
   * ## 报价按模板参数**现问一次**
   *
   * 页面上那份 `price` 是对**表单**参数报的，与本模板的时长 / 比例可能不同
   * （用户未必把模板套用到表单上）。按秒计价，拿表单那份去弹确认框，价格会与
   * 真实扣费差出好几倍 —— 所以在提交前单独问一次 `pricePreview`。
   *
   * ## 完成后挂回模板
   *
   * 走 `onDone`（见 `useGenerateFlow`）：跑完（成 / 败都算）拿产出地址
   * `update(tpl.id, { sampleUrl })`，并顺手把抽屉里那份 `previewTpl` 快照也换掉，
   * 让用户当场就看到片子。`keepWorkspace: true` —— 出案例片是辅助产物，
   * 不该清掉用户正在写的主表单。
   */
  const generateSample = async (tpl: AnyVideoTemplate) => {
    if (!form.productName.trim()) {
      toast.error('先填产品名 —— 案例片要拿它当主角，没有它模型只能编一个产品')
      return
    }
    /* 案例片的提示词：**参数逐项来自模板**，产品 / 卖点来自表单 */
    const tplPrompt = buildVideoPrompt({
      ...form,
      scene: tpl.preset.scene,
      style: tpl.preset.style,
      duration: tpl.preset.duration,
      aspectRatio: tpl.preset.aspectRatio,
      storyboard: tpl.storyboard,
      /* 用模板自己的原始分镜（不叠当前表单的镜头细化 —— 那是为另一个产品写的） */
      shotNotes: [],
      refStoryboard: false,
      refVideoCount: 0,
      videoAnalysis: null,
      extra: [form.extra, templateShotLine(tpl)].filter(Boolean).join('\n'),
    })
    /* 报价按模板参数现问一次（见上面的注释）；问不到就按 0 报，确认框仍会展示参数 */
    const quote = await api
      .pricePreview({
        appSlug: meta.appSlug,
        model: form.model,
        outputs: 1,
        resolution: form.resolution,
        aspectRatio: tpl.preset.aspectRatio,
        inputs: { duration: tpl.preset.duration },
      })
      .catch(() => null)
    flow.request({
      info: {
        title: `案例片 · ${templateTitle(tpl)}`,
        appSlug: meta.appSlug,
        model: form.model,
        outputs: 1,
        aspectRatio: tpl.preset.aspectRatio,
        resolution: form.resolution,
        /* 案例片是纯文字出片（与内置案例片同口径，不带参考图） */
        refImages: 0,
        prompt: tplPrompt,
        price: quote?.estimatedPrice ?? 0,
        basis: quote?.basis,
        free: quote?.isFreeThisCall,
        platform: platform.name,
      },
      args: {
        appSlug: meta.appSlug,
        title: `案例片 · ${templateTitle(tpl)}`,
        sourcePage: pageKey,
        model: form.model,
        prompt: tplPrompt,
        /* `__templateId` 只用于记录溯源（历史里能看出这条片子是替哪条模板出的） */
        inputs: { ...form, __title: '案例片', __templateId: tpl.id },
        imageUrls: [],
        videoUrls: [],
        aspectRatio: tpl.preset.aspectRatio,
        resolution: form.resolution,
        outputs: 1,
        estimatedMs: 240000,
      },
      keepWorkspace: true,
      onDone: (done) => {
        const url = done
          .flatMap((j) => j.outputs)
          .find((o) => o.type === 'video' && o.url)?.url
        /* 失败就不动模板（`fix_template_samples.py` 那条口径：宁可没有，也不要挂错的） */
        if (!url) return
        void updateVideoTpl(tpl.id, { sampleUrl: url }).then(
          () => {
            /* 抽屉里那份是快照，手动补上 sampleUrl —— 用户当场就能播 */
            setPreviewTpl((cur) => (cur && cur.id === tpl.id ? { ...cur, sampleUrl: url } : cur))
            toast.success('案例片已生成，并挂到这条模板上')
          },
          (e) =>
            toast.error(
              `案例片已生成，但挂回模板失败：${e instanceof Error ? e.message : String(e)}`,
            ),
        )
      },
    })
  }

  /* ------- 页签 · 分析视频（**只在复刻档**；它是复刻档的第 1 位） ------- */

  /**
   * 「分析视频」= 传参考视频 + 让 AI 拆出它的镜头结构。
   *
   * 2026-10-08 第二轮按用户要求新增。用户原话：「上传后应该有分析按钮啊，
   * 都说第一部分是分析视频，那些生成引擎跟产品在 step 2」——
   * 也就是把原来的「引擎与素材」一拆为二：参考视频单独成第 1 页，
   * 引擎与产品退到第 2 页。
   *
   * **为什么值得单独占一页**：传完片子之后，用户真正要回答的第一个问题是
   * 「这条片子是怎么拍的」（几个镜头、多快、什么景别）。这个答案会进提示词，
   * 后面几页全围着它转。上一版把它塞在「引擎与素材」里，用户会先被引擎下拉拦住，
   * 直到出片才发现结构没对上 —— 而那时钱已经花了。
   *
   * 所以这一页**不放引擎、不放产品图**：它们跟「看懂这条片子」不是同一件事，
   * 摆在这里只会把上传框挤到折线以下（那正是上一版的样子）。
   *
   * ⚠️ 这里保留 `needVideo || refVideoMax > 0` 这个条件：本页目前只在复刻档
   * 出现（那里 `needVideo` 恒真），但写死「一定渲染」的话，将来某个档把它捡起来
   * 就会出现「让用户传了、上游却忽略」——那是比直接不给更糟的体验。
   */
  const stepAnalyze = (
    <>
      {(needVideo || refVideoMax > 0) && (
        <Card>
          <CardHead
            title="参考视频"
            icon="video"
          />
          <CardBody>
            <RefUploader
              wrap="none"
              kind="video"
              /* 点缩略图预览时，遮罩标题用这个；不传会落到默认的「参考图预览」 */
              title="参考视频"
              urls={form.refVideo}
              onChange={(urls) => patch({ refVideo: urls })}
              max={needVideo ? 1 : refVideoMax}
              /* 视频**没有「主图」这个语义**（那是产品图的概念）—— 与「场景参考图」
                 同一个理由，见 RefUploader 的 `firstBadge` 说明 */
              firstBadge=""
              dropLabel="拖拽或点击上传参考视频"
              dropHint="MP4 / MOV · 上传后存到腾讯云 COS，公网可直接取回"
            />
          </CardBody>
        </Card>
      )}

      {/*
        镜头结构卡。`videoUrl` 传的是**公网绝对地址** —— 上游 apilio 是从公网把
        这条片子拉过去读的，站点相对路径它取不到；传错的症状不是报错，而是
        「一次十几秒的调用干等满超时」。视频上传走 COS 直链，所以这里不需要做
        任何地址转换（见上面 `analysisVideoUrl` 的说明）。
      */}
      <VideoAnalysisCard
        videoUrl={analysisVideoUrl}
        analysis={form.videoAnalysis}
        onChange={(next) => patch({ videoAnalysis: next })}
        sourcePage={`${pageKey} · 分析视频`}
      />
    </>
  )

  /* ------- 页签 · 产品与要求（**只在复刻档**；它是复刻档的第 2 位） ------- */

  /**
   * 「产品与要求」= 传产品图 + 写一段要求。复刻档砍到这一步为止。
   *
   * 2026-10-09 第二轮按用户要求新增，取代了上一版的「引擎与内容」（引擎 + 产品图 +
   * 产品名称 + 卖点 + 场景参考图）**和整个「输出参数」页**。用户原话
   * 「我觉得一个框跟上传图片就可以」「第一图的生成 AI 提示词，结构跟脚本
   * 要用分析视频的基础去生成」。
   *
   * ## 为什么只剩这两样
   *
   * 复刻档的输入物是**一条参考视频**，其余全是「把这条片子的产品换成我的」：
   *   · 生成引擎 → 退成默认模型（页面上不再出现下拉）；
   *   · 复刻方式 → 固定「结构复刻」（照参考视频的镜头顺序与切换时机走）；
   *   · 产品名称 → 不再单列，跟卖点一起写进下面那个要求框
   *     （产品是什么由 AI 从这段文字里认出来）；
   *   · 场景参考图 → 画面氛围本来就由参考视频那条片子决定，再传一张是两套风格打架；
   *   · 输出参数 → 只留时长 / 比例 / 清晰度 / 平台四项花费相关的，收在**本页卡片底部**
   *     那行（2026-10-09 第三轮：此前它浮在页签外的顶上，用户口径「放回去」→ 归位到本页）。
   *
   * ## 「一个框」写进哪个字段
   *
   * 写进 `sellingPoints`（原「卖点与补充要求」）—— 它同时喂三处：
   * 提示词里的【卖点】、按平台档案推出来的【卖点组织方式】，以及
   * 「AI 生成提示词」那份汇总上下文。**不另开新字段**：新开一个
   * 只在复刻档出现的字段，等于让同一件事（用户写的要求）有两个容器，
   * 提交、草稿、指纹三处都得跟着分叉。
   *
   * ⚠️ 产品名**刻意留空**（用户口径「不给产品名，AI 从文字里自己认」）。
   * 提示词里的【产品】会是模型给的占位符；把这句要求写全（是什么产品、
   * 要讲什么、画面上的额外要求）比拆成两个半空的字段更好。
   */
  const stepBrief = (
    <Card>
      <CardHead
        title="产品与要求"
        icon="text"
      />
      <CardBody>
        {(!needVideo || refImageMax > 0) && (
          <RefUploader
            wrap="none"
            kind="image"
            label="产品图"
            fieldHint={`只用于还原产品外观（颜色 / 材质 / 结构），复刻时替换掉原片里的产品 · 最多 ${refImageMax} 张`}
            urls={form.refs}
            onChange={(urls) => patch({ refs: urls })}
            max={refImageMax}
            dropLabel="拖拽或点击上传产品图"
            dropHint={`支持多选 · 可拖拽排序 · 第一张作为主图${refImageMax ? ` · 本模型最多 ${refImageMax} 张` : ''}`}
          />
        )}

        <Field
          label="要求"
          hint="一行一条。写清这三件事：① 这是什么产品 ② 这条片子要讲什么 ③ 画面上的额外要求。产品名也直接写在里面，AI 会从这段文字里认出来。"
        >
          <Textarea
            rows={6}
            value={form.sellingPoints}
            placeholder={'产品：华数云折叠沥水垫\n要讲：沥水快、能折叠收纳\n画面：结尾收在完整产品'}
            onChange={(e) => patch({ sellingPoints: e.target.value })}
          />
        </Field>

        {/*
          输出参数 —— 从整页砍到只剩四项花费相关的（2026-10-09 第二轮）。
          2026-10-09 第三轮：这几项原浮在**页签外的顶部**（与档位 chip-row 同一格），
          用户口径「放回去」→ 归位到本页卡片底部，与「产品与要求」同屏。

          ⚠️ 三项 chip 的候选**必须取模型能力过滤后的清单**（`resOptions` / `ratioOptions`）：
          复刻档不再让用户选引擎，模型仍是默认那个，写死 480/720/1080 会在只收 768P 的
          模型上给出上游不认的档位（提交时才报错）。清晰度 label 走 `resolutionLabel()`
          归一化、value 用模型原值 —— 与「输出参数」页同一条口径（上游大小写不统一）。
        */}
        <div
          className="chip-row"
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: '1px solid hsl(var(--border))',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <span className="text-xs muted">时长</span>
          {/* 与「输出参数」页同一份控件与同一组模型范围（复刻档也常驻一个数字框） */}
          <DurationInput
            value={form.duration}
            onChange={(n) => patch({ duration: n })}
            presets={durPresets}
            min={durMin}
            max={durMax}
            allowCustom={!modelInfo?.capabilities?.duration_options?.length}
          />

          <span className="text-xs muted">比例</span>
          {/* 与「输出参数」页同一份控件：下拉（本模型能出的比例）+ 自由输入框 */}
          <RatioInput
            value={form.aspectRatio}
            onChange={(v) => patch({ aspectRatio: v })}
            options={ratioOptions}
          />

          <span className="text-xs muted">清晰度</span>
          <Chips
            value={form.resolution}
            onChange={(v) => patch({ resolution: v })}
            options={resOptions.map((r) => ({ value: r, label: resolutionLabel(r) }))}
          />

          <span className="text-xs muted">平台</span>
          <Select
            value={form.platform}
            onChange={(e) => patch({ platform: e.target.value })}
            style={{ width: 'auto', minWidth: 120 }}
          >
            <optgroup label="国内">
              {DOMESTIC_PLATFORMS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="海外">
              {OVERSEAS_PLATFORMS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </optgroup>
          </Select>
        </div>
      </CardBody>
    </Card>
  )

  /* ------- 页签 · 内容与场景（**只在合并页**） ------- */

  /**
   * 「内容与场景」= 成片**讲什么**（产品名 / 场景 / 卖点）**+ 长什么样**（产品图 / 场景图）。
   *
   * 「产品参考图」2026-10-09 从「引擎与素材」页搬到这里（用户口径「产品参考图要放到
   * 内容跟场景」）—— 那一页搬空后整页撤掉，引擎也另去了「视频模板」卡。
   * 它与本页的场景参考图**不是同一件事**：
   *   产品参考图 → 还原**这个东西**（颜色 / 材质 / 结构），是「标的物」
   *   场景参考图 → 取**这张片子**的氛围、构图与光线，是「画面风格」
   * 所以仍是两个字段、两个上传器，提交时按「产品图在前、场景图在后」拼进同一个
   * `imageUrls`，并在提示词里逐段写明各是哪张 —— 不写清楚，
   * 模型会把场景图里的人或桌面当成要复刻的东西。
   */
  /**
   * 「AI 填充」不让点的原因（有值 = 禁用，不隐藏）。
   *
   * 前置条件只有**产品名**一条 —— LLM 是纯文本的（看不了参考图），
   * 没有产品名它只能编，而编出来的卖点看着像真的，用户会一路用到出片才发现
   * 产品是它造的（同 `/banner` 那条）。**不禁用「没有卖点」**：那一格本来就是
   * 让 AI 从零补的，空着才是常态。
   */
  const videoFillBlocked = !form.productName.trim()
    ? '先填产品名称 —— AI 需要知道这是什么产品才写得对'
    : undefined

  /**
   * 产品参考图这一格该不该出现 —— 原判据 `(!needVideo || refImageMax > 0)`
   * （复刻档允许上传，或本模型有图片额度）。抽成常量是因为合并进
   * `ProductInfoSection` 之后它要用在 `onRefsChange` 上（不传即不渲染那一格）。
   */
  const showProductRefs = !needVideo || refImageMax > 0

  const stepContent = (
    <>
      {/* 「内容与场景」—— 产品名 / 卖点 / 产品图走全站唯一一份 `ProductInfoSection`
          （2026-10-10 用户口径：产品信息在很多页被拆散，合成一个组件）。
          产品名与产品图此前分踞两张卡（「内容与场景」+「产品参考图」），填一项要来回找；
          现在同卡。⚠️「场景参考图」不参与产品还原（它取的是画面氛围与光线），
          所以作为**追加字段**紧跟产品信息之后，仍是独立的一格。 */}
      <ProductInfoSection
        title="内容与场景"
        icon="text"
        headRight={
          /* 「AI 填充」—— 用户只给产品名，卖点与补充要求由 AI 按本页上下文补齐。
             与最后一个页签那颗「AI 细化镜头」不是一件事：那颗改的是**镜头层**
             （每段拍什么，分镜图提示词与视频提示词共用同一份，改完手上的九宫格
             会失效），这颗改的是**表单字段**（改完提示词自动跟着重算，但那张
             九宫格不会失效 —— 它比的是提示词，而提示词确实变了…所以 toast 里
             要说清「分镜图可能要重出」）。按钮实现与 `/banner` 共用一份。 */
          <AiFillButton<FormState>
            kind="video"
            /* 白名单取自跨前后端的真源 —— 与后端 `_FILL_FIELDS["video"]` 逐字一致，
               键名 `sellingPoints` 就是这一页表单里那一格的字段名（`AiFillButton`
               再挡一道，模型多返回的键进不来）。 */
            fields={FILL_FORM_FIELDS.video}
            brief={buildVideoFillBrief({
              productName: form.productName,
              scene: form.scene,
              /* 与成片提示词同一处分档：复刻档的 scene 是复刻方式，不是画面场景。
                 上下文里写「【视频场景】结构复刻」，模型会去写一个不存在的场景。 */
              sceneLabel: isReplicate ? '复刻方式' : undefined,
              sellingPoints: form.sellingPoints,
              extra: form.extra,
              duration: form.duration,
              aspectRatio: form.aspectRatio,
              style: form.style,
              platform: form.platform,
              templateName: tpl ? templateTitle(tpl) : undefined,
              refCount: form.refs.length,
              /* 镜头脚本一起给 ——「卖点长度要配得上分镜」这句话得有配的对象
                 （15 秒 6 镜头与 8 秒 3 段，卖点写法完全两样） */
              storyboard: tpl?.storyboard,
              /* 绑定后的镜头描述 —— 与分镜图提示词、成片提示词同源；这里给旧的会
                 让 AI 照着上一个产品的镜头去补卖点 */
              shotNotes: sbNotes,
            })}
            sourcePage={pageKey}
            /* 快照必须在**覆盖前**取 —— 组件里再取就已经是 AI 写过的版本了 */
            snapshot={{ sellingPoints: form.sellingPoints }}
            onFill={patch}
            blocked={videoFillBlocked}
            label="AI 补卖点"
          />
        }
        name={form.productName}
        onNameChange={(v) => patch({ productName: v })}
        namePlaceholder="如：华数云折叠沥水垫"
        nameRequired={!needVideo}
        /* 「从商品库选择」—— 商品库录好的档案一次带进来（标题 + 卖点 + 产品图）。
           ⚠️ 图进的是这一页的**产品图**槽位，且受「产品图 + 场景图」共享额度约束
           （见 `applyProductAsset`）。复刻档产品名非必填，但带进来照样有用 ——
           它同时是 AI 补卖点的入口字段。 */
        onPickAsset={applyProductAsset}
        description={form.sellingPoints}
        onDescriptionChange={(v) => patch({ sellingPoints: v })}
        descriptionLabel="卖点与补充要求（可选）"
        descriptionHint="一行一条；画面上的额外要求也写这里"
        descriptionPlaceholder={'150AW 大吸力\n轻至 1.2kg，单手可举\n结尾收在完整产品'}
        refs={form.refs}
        onRefsChange={showProductRefs ? (urls) => patch({ refs: urls }) : undefined}
        refsLabel="产品参考图"
        refsHint="管「产品长什么样」；想控画面氛围，用下面那张场景参考图"
        refsMax={refImageMax}
        refsDropLabel="拖拽或点击上传产品图"
        refsDropHint={`支持多选 · 可拖拽排序 · 第一张作为主图${refImageMax ? ` · 本模型最多 ${refImageMax} 张` : ''}`}
      >
        {/*
          2026-10-09：这排「视频场景」chip **已撤**（用户原话「『视频场景』那条不需要，
          模板已经有」）。对照清单他说得对 —— 这排 chip 驱动的其实只有一个词槽：
          `prompts.ts` 只看 `scene.includes('口播')` 决定要不要打开口播，其余选项
          做的唯一一件事就是往提示词里塞一行 `【视频场景】xxx`；真正决定片子长什么样
          的是**模板**（分镜表 + 全片硬约束 + 场景 / 风格 / 时长 / 比例这几项），
          而模板卡标题里这几项已经写全了。

          撤它的**前置条件**是 12 个场景全部有模板承载 —— 补库五条（vt-11..15）
          落齐之后才成立，由 `smoke-video-templates` 的「场景承载」断言钉住：
          缺一个场景，那条断言会红，而不是让用户在一个永远走不到的选项上孤零零地迷路。
          （复刻档那排「复刻方式」chip 也已在 2026-10-09 第二轮撤掉 ——
          那一档固定按「结构复刻」走，这一页本来就只在合并页渲染。）
        */}
        {sceneRefMax > 0 ? (
          <RefUploader
            wrap="none"
            label="场景参考图（可选）"
            fieldHint={`只取场景与光线，不参与产品还原 · 本页还可传 ${sceneRefMax} 张`}
            urls={form.sceneRefs}
            onChange={(sceneRefs) => patch({ sceneRefs })}
            max={sceneRefMax}
            dropLabel="拖拽或点击上传场景参考图"
            dropHint="建议只传 1 张 —— 多张混在一起模型会各取一半"
            /* 场景图没有主副之分（它不参与产品还原），别挂「主图」角标 */
            firstBadge=""
            emptyHint="模型只学它的场景感与光线，不会把里面的人或产品搬进来。"
          />
        ) : (
          <Field label="场景参考图（可选）" hint="本模型的参考图额度已被产品图占满">
            <div className="text-xs muted">
              参考图上限是 {refImageMax} 张，已经全部用在下面那张
              <b>产品参考图</b>上。想加场景图，先移除几张。
            </div>
          </Field>
        )}
      </ProductInfoSection>
    </>
  )

  /* ------------------------- 页签 · 分镜参考图（仅合并页） ------------------------- */

  /**
   * 状态徽章 —— 折叠头（收起态）与 Field 标签行（展开态）**共用这一份**。
   *
   * ⚠️ 状态**必须由徽章自己说**：出图后这个区是**折叠**的，hint 根本不在屏幕上 ——
   * 而「现在我看到的这份提示词是哪来的」恰恰是折叠态下最该被看见的一件事。
   * 所以「按参数自动 / 我的版本 / AI 细化镜头」三态之外，还要有第四态
   * 「已按当前参数更新」：手改/细化过、但输入已经变了、那份不再生效。
   * 少了这一态，用户会看着一份自己没见过的提示词，以为系统把他的东西弄丢了。
   */
  const sbPromptBadge = (
    <Badge
      tone={sbEditExpired ? 'info' : sbCustomPrompt ? 'neutral' : sbNotes.length ? 'success' : 'neutral'}
    >
      {sbEditExpired
        ? '已按当前参数更新'
        : sbCustomPrompt
          ? '我的版本'
          : sbNotes.length
            ? 'AI 细化镜头'
            : '按参数自动'}
    </Badge>
  )

  /**
   * 分镜提示词输入框 —— **只有一份实现**：出图前（整栏全宽）与出图后（预览下方折叠区）
   * 共用同一份，差别只有行数与标签。写成两份的话，hint 与「恢复自动生成」的禁用判据
   * 会在两个状态里各说各话（本项目「同一控件只许一份实现」的复发点）。
   *
   * `bare` = 出图后把它折进折叠区时用：标签与徽章已经由折叠头承载，
   * 这里就不再重复渲染那一行（同一个字段在屏幕上出现两次标题是噪音）。
   * ⚠️ 动作行**不在这里**渲染（见 `sbPromptActions`）—— 出图后必须留在折叠区外。
   */
  /**
   * 分镜提示词的三颗动作 —— **只有一份实现**，两种排版共用：
   *   · 没出图：跟在输入框下面（由 `sbPromptField` 渲染）；
   *   · 出过图：**常驻在折叠区之外**（由 `stepStoryboard` 直接渲染）。
   *
   * ## 为什么出图后必须搬出来（2026-10-05 用户报障）
   *
   * 用户原话：「为什么视频脚本不可以用 ai 生成多一次？」
   * 能力一直都在 —— 「AI 细化镜头」写的就是视频提示词里那段【镜头脚本】
   * （两条提示词共用 `effectiveStoryboard` 这一份镜头数据）。问题是它跟输入框
   * 一起被折进了「分镜提示词」，而那个折叠区**默认是收起的**：
   * 一旦出过图，用户连按钮的 DOM 都摸不到，自然得出「不能用」的结论。
   *
   * 这条判断本屏早就有了（见下面「按这份提示词重新生成」旁边的注释）：
   * **动作要留在折叠区外常驻 —— 折起来用户就找不到。** 上一版只把
   * 「重新生成」搬了出去，漏了这三颗。
   */
  const sbPromptActions = (
    <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
      <Button
        variant="ghost"
        size="sm"
        icon="sparkles"
        loading={sbShotsBusy}
        /* 一次 ¥0.10（后端按条计价并上账）。**刻意不弹价格确认框** ——
           这是「改输入」的轻量动作，为它挡一道弹窗比这一毛钱更烦人；
           花了多少由成功后的 toast 如实报出（含模型名与耗时）。 */
        onClick={doAiShots}
      >
        {sbShotsBusy ? 'AI 正在细化…' : 'AI 细化镜头'}
      </Button>
      <CopyButton text={sbPrompt} label="复制提示词" />
      <Button
        variant="ghost"
        size="sm"
        icon="refresh"
        /* 没改过就没什么可恢复的。**禁用而不是藏起来** —— 让「这里能回退」一直看得见。
           ⚠️ 判据要**同时看 `shotNotes`**：AI 细化的产物存在那里，只清
           `storyboardPrompt` 的话细化出来的镜头会留着，按钮看着像没反应。 */
        disabled={!sbCustomPrompt && !sbNotes.length && !sbEditExpired}
        onClick={() => patch({ storyboardPrompt: '', shotNotes: [], sbInputKey: '' })}
      >
        恢复自动生成
      </Button>
    </div>
  )

  const sbPromptField = (rows: number, bare = false) => (
    <Field
      label={bare ? undefined : '分镜提示词'}
      hint={
        sbEditExpired
          ? '你之前手改 / AI 细化的那一版是按旧参数写的，参数一变换就不适用了 —— 这里已自动切回按当前参数生成。把产品与参数改回原样，还能找回那一版。'
          : sbCustomPrompt
            ? '已改成你的版本：改动上面任何一项产品或参数都会自动切回按参数生成（不会像以前那样一直压着自动版），要立刻回退就点「恢复自动生成」。'
            : sbNotes.length
              ? '镜头描述已由 AI 细化过（与提示词共用同一份）；直接在这里改就变成你的版本，点「恢复自动生成」可以退回模板原版。'
              : `按「${paneTitle('content')}」页的内容与场景 + 本页参数自动生成；点「AI 细化镜头」能把每段画面写到可拍摄，直接在这里改就变成你的版本。`
      }
      right={bare ? undefined : sbPromptBadge}
    >
      <Textarea
        rows={rows}
        value={sbPrompt}
        onChange={(e) =>
          /* 手改的**同时**记下它是为哪组输入写的 —— 这一格就是「跟着产品走」的全部实现。
             不记的话这份文字会永久压住自动版（2026-10-05 报障）。 */
          patch({ storyboardPrompt: e.target.value, sbInputKey: sbInputKeyNow })
        }
      />
      {/* 折进折叠区时动作不跟着进去 —— 出图后它们由 `stepStoryboard` 渲染在折叠区外 */}
      {bare ? null : sbPromptActions}
    </Field>
  )

  /**
   * 「分镜参考图」= 先用生图模型把 9 个镜头画出来核对，再花视频的钱。
   *
   * 为什么要单独一屏、而不是并进「参数与生成」：用户原话是
   * 「输出产品跟要求，第二步输出分镜图（9 宫格），最后再生成视频」——
   * 它是一次**独立产出**（花一次生图的钱、等一次出图、不满意会重出），
   * 和「选平台/比例/时长」那种改完立刻生效的参数不是一类东西。
   *
   * 这一步的经济意义：视频按秒计价，一条 15 秒 ¥4.50 上下，出来才发现镜头
   * 不对只能整支重跑；先花 ¥0.10 看一眼镜头顺序与构图，是这条链路上最便宜的
   * 一次纠错机会。所以按钮、进度、出图、重出都留在这里，不塞进参数堆。
   *
   * 出图后**不在这里重复列那 9 格文字** —— 右栏那张「分镜脚本」卡本来就常驻，
   * 写的是同一份 `effectiveStoryboard`；两处都列就是同一件事说两遍，
   * 而且改时长时两处都可能漏改一处。
   */
  const stepStoryboard = (
    <Card>
      <CardHead
        title="分镜参考图"
        icon="film"
      />
      <CardBody>
        {sbRunning ? (
          <div className="stack stack--sm">
            <Progress value={sbRunning.progress} indeterminate={sbRunning.progress <= 0} />
            <div className="text-xs muted">正在出分镜参考图，约半分钟。</div>
          </div>
        ) : sbUrl ? (
          /*
            预览在上、提示词区在下（2026-09-30 用户要求「放到下面」）。

            此前是左右并排（并排的理由只是把按钮顶回首屏）。改成上下之后，
            **提示词编辑区默认收起** —— 收起态只多一行折叠头，整体高度与并排时
            基本持平，按钮不至于掉出首屏；要改提示词点那行展开即可。

            折起来的**只有那段文字**。动作一律留在折叠区外常驻：
            「AI 细化镜头」+「复制提示词」+「恢复自动生成」（`sbPromptActions`）、
            「重新生成」、以及出图状态说明。
            判据是「折起来用户就找不到的东西不能折」——
            「AI 细化镜头」恰恰是**出图之后最该被看见的那颗**（2026-10-05：
            用户出完图想问「为什么视频脚本不能用 AI 再生成一次」，
            而它当时正躺在收起态的折叠区里，DOM 都没有）。
          */
          <div className="sbresult">
            {/*
              分镜九宫格**点开放大**（2026-10-08 用户原话：「可以点开」）。

              为什么非点开不可：这张图在这块区域里只有 240px 宽，九宫格摊下来
              每格 ≈80px —— 「第 4 格的手有没有挡住产品」「第 7 格的字是不是歪的」
              这类要看的东西，在缩略图上根本判断不了，而核对镜头恰恰是这一步
              存在的**唯一理由**。

              放大走结果区那份 `ImageLightbox`（点背景 / Esc 关闭、锁背景滚动、
              portal 到 body 避开卡片 `overflow: hidden` 全都在它一份实现里），
              不自己再拼一个遮罩 —— 本项目「交互控件只许一份实现」。
            */}
            <button
              type="button"
              className="sbpreview"
              onClick={() => setSbZoom(true)}
              title="点开放大，逐格核对九个镜头"
            >
              <SmartImage src={sbUrl} alt="九宫格分镜参考图" />
              <span className="sbpreview__hint">
                <Icon name="expand" size={12} />
                点开放大
              </span>
            </button>
            <div className="sbresult__side">
              {/* 折叠头一律用 `CardHead`（整块可点 / aria-expanded / 键盘可达 / chevron
                  只有那一份实现，smoke-nav 会拦住自己拼 summary 的写法）；
                  容器只负责把它的默认 padding 收掉，见 global.css 的 `.inlinefold` */}
              <div className="sbfold inlinefold">
                <CardHead
                  title="分镜提示词"
                  open={sbPromptOpen}
                  onToggle={() => setSbPromptOpen((v) => !v)}
                  right={sbPromptBadge}
                />
                {sbPromptOpen ? sbPromptField(6, true) : null}
              </div>
              {/* 三颗动作：改提示词 / 让 AI 写镜头脚本 / 复制 —— **不随折叠收起** */}
              {sbPromptActions}
              {/* 把「这颗按钮改的是哪份东西」说透：用户看的是生成视频时那段【镜头脚本】，
                  而按钮长在「分镜提示词」旁边 —— 不点明就没人知道它俩是同一份数据
                  （2026-10-05 用户原话：「为什么视频脚本不可以用 ai 生成多一次」）。
                  ⚠️ 这里不能写 `**`：本项目没有 markdown 渲染器，星号会原样显示出来。 */}
              <div className="text-xs muted">
                「AI 细化镜头」写的就是右栏那张「分镜脚本」卡 —— 它和生成视频时提示词里的
                【镜头脚本】是同一份数据，改一次两处一起变。
              </div>
              <div className="text-xs muted">
                {/* 出图时间不是装饰：这张图可能来自上一次会话（判据是提示词逐字一致，
                    不是"刚刚生成"），不写时间用户会以为它是刚出的、甚至以为是别人的图。 */}
                {sbDone ? `${fmtTime(sbDone.createdAt)} 出图 · ` : ''}
                {refPlan.storyboard
                  ? '它就是生成视频唯一的参考图，会随下一次「生成视频」提交。'
                  : `本模型（共 ${refImageMax} 张参考图额度）不接受这张图，生成视频不会带上它 —— 到「视频模板」换一个支持参考图的模型再生成。`}
              </div>
              {/*
                两颗动作并排：重出分镜图 / 把这一整套存成模板。

                「存为模板」刻意放在**看到这张九宫格之后**：用户口径是
                「如果保存这个过程，可以抽结构出来存储到模板中心」——
                他得先看到分镜与成片，才知道这套值不值得存。
                封面直接取手上这张九宫格（后端会转存，见 `saveTemplate`）。
              */}
              <div className="row">
                <Button variant="ghost" size="sm" icon="refresh" onClick={doStoryboard}>
                  按这份提示词重新生成
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  icon="star"
                  onClick={openSaveTemplate}
                  title="把当前这套参数、分镜脚本与全片约束存成自己的模板；封面用这张九宫格，内容带上本页最近这条成片"
                >
                  存为模板
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs muted" style={{ marginBottom: 10 }}>
              两步：先按
              {isReplicate
                ? `「${paneTitle('brief')}」页那段要求（连同右栏那份镜头脚本）`
                : `「${paneTitle('content')}」页的产品名称 / 场景 / 卖点（连同右栏那份镜头脚本）`}
              自动写一份提示词，你可以直接改；再用它出一张 3×3 九宫格 ——
              每格是这支片子的一个镜头，从左上到右下按时间走。
              这一张是给人核对的，也是生成视频唯一的参考图（产品图 / 场景图在这一步带进去），不满意可以重出。
            </div>
            {sbPromptField(10)}
            <div style={{ marginTop: 12 }}>
              <Button icon="sparkles" onClick={doStoryboard}>
                生成分镜参考图
                {sbPrice?.estimatedPrice ? ` · ¥${sbPrice.estimatedPrice.toFixed(2)}` : ''}
              </Button>
            </div>
          </>
        )}
        {/*
          两种「手上那张不算数」要**分开说** —— 合起来说一句「已失效」，
          用户就分不清是「参数变了、要重出」还是「已经用掉了、本来就该没」：
            · `sbUsed`＝已经随「生成视频」交出去，按用户要求清空（2026-10-05）；
            · `sbStaleUrl`＝上一版提示词画的（参数变过）。

          两者都**不渲染预览、不进参考图**，只提一句。用户报障原话就是
          「这张怎么一直在的」：它明明与当前参数无关，却占着预览位、还被当成「已带上」。
        */}
        {sbUsed ? (
          <div className="text-xs muted" style={{ marginTop: 12 }}>
            上一张分镜图已经随「生成视频」用掉，按你的要求清空了 —— 不再显示，也不会再带进下一条片子。
            要给下一条片子核对镜头，点上面的按钮重出一张。
          </div>
        ) : null}
        {sbStaleUrl ? (
          <div className="text-xs muted" style={{ marginTop: 12 }}>
            上一次出的那张分镜图是按改动前的提示词画的，已经不算数：不显示大图，也不作为参考图带上。
            点上面的按钮就会按现在这份提示词重出一张。
          </div>
        ) : null}
        {sbFailed ? (
          <div className="text-xs" style={{ marginTop: 10, color: 'hsl(var(--destructive))' }}>
            上一次出图失败：{sbFailed.error?.message ?? '未知原因'}。可以再点一次重出。
          </div>
        ) : null}
      </CardBody>
    </Card>
  )

  /* ------- 页签 · 输出参数（合并页第 1 位；复刻档第 2 位，见 PANES 头注释） ------- */

  const stepParams = (
    <>
      <Card>
        <CardHead
          title="输出参数"
          icon="bolt"
        />
        <CardBody>
          {/* 短控件两两并排：并排一对省约 90px 纵向高度 */}
          <div className="field-row">
            <Field label="投放平台" hint={`${platform.name} · 通版 ${platform.sizes.video}`}>
              <Select value={form.platform} onChange={(e) => patch({ platform: e.target.value })}>
                <optgroup label="国内">
                  {DOMESTIC_PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="海外">
                  {OVERSEAS_PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </Field>

            <Field label="视觉风格">
              <Select value={form.style} onChange={(e) => patch({ style: e.target.value })}>
                {VIDEO_STYLES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="field-row">
            <Field
              label="清晰度"
              /*
                ⚠️ 分两种说法（2026-10-10）。
                原来一律写「X 支持 480P / 720P / 1080P」—— 而那是 `resOptions` 的
                **兜底清单**：模型没有 `capabilities.resolutions` 时它才顶上来的
                （见上面的注释）。于是速创那两档（Google Omni / MiniMax）会看到
                「Google Omni 支持 480P / 720P / 1080P」，而它其实**不按清晰度档出片**：
                它自己按比例定一个分辨率（见 `skills/wuyin.py` 的 `_SIZE_MAP`），
                上传的清晰度值一个都不用、报价也不随它变。

                这句话原来只是下拉里一个可选档上的小字，没人细看；
                2026-10-10 起 Google Omni 是**默认引擎**，它就成了首屏上的一句假话 ——
                用户会以为自己选的 720P 真的决定了画质。所以按「模型有没有给
                `resolutions`」分岔：给了就照旧列清单，没给就说清「这一项它不认」。
              */
              hint={
                allowedRes?.length
                  ? `${modelInfo?.name ?? '当前模型'}支持 ${resOptions.map(resolutionLabel).join(' / ')}`
                  : `${modelInfo?.name ?? '当前模型'}不按清晰度档出片（它按画面比例自行定分辨率）—— 这一项对它不影响报价`
              }
            >
              <Segmented
                value={form.resolution}
                onChange={(v) => patch({ resolution: v })}
                /*
                  ⚠️ **value 用模型的原值，label 才归一化**：
                  上游各模型大小写不统一（同一个平台里 720P 与 720p 并存），
                  直接渲染会在同一排里出现两种长相；但提交必须是模型认的那个原值，
                  改大小写会被 `_guard_value()` 判成非法值。
                */
                options={resOptions.map((r) => ({ value: r, label: resolutionLabel(r) }))}
              />
            </Field>

            <Field label="画面比例" hint="下拉是本模型能出的比例；旁边也能自己填（如 21:9）">
              {/*
                2026-10-08 由 `Segmented`（一排按钮）改成下拉；
                2026-10-09 再加一个**自由输入框**（用户口径「能自由填比例」）——
                详见 `RatioInput` 的文件注释。

                为什么保留下拉：它是这一页**选项最多**的控件，实测 9 个
                （sd 系更多，21:9 / 3:2 / 2:3），一排横过去占掉小半屏，也是视频
                工作台首屏「58 个可点元素」里单一来源最大的一处（9 个）。下拉负责
                「常见的那几种一点就中」，输入框负责「下拉里没有的」。

                ⚠️ 清晰度（最多 3 个）**保持分段控件**，不是漏了：
                选项少时「一眼看全 + 一点就中」比下拉快。这条界线是**选项数**，
                不是「统一风格」—— 一并有下拉会把好用的那个也降级。
                （运镜已于 2026-10-09 按用户口径整项删除，见文件头与 `videoModes.ts`。）

                ⚠️ 选项文字**只用比例原值**，不套 `ratioLabel()`：
                那个函数的用途说明是**图片语境**的（`16:9 · 横幅 · A+ 宽图 · 视频封面`），
                贴在视频工作台上会把「横版视频」说成 A+ 图。
              */}
              <RatioInput
                value={form.aspectRatio}
                onChange={(v) => patch({ aspectRatio: v })}
                options={ratioOptions}
              />
            </Field>

          </div>

          {/*
            时长单独一行且带价格提示 —— 它是本页唯一「一改就变价」的控件。

            2026-10-09 用户口径「时长是可以自定义的」：档位旁常驻一个数字框，
            想要 12 秒就敲 12。可敲的范围**由所选模型的上游时长能力决定**
            （`capabilities.duration_min / duration_max`），只认固定几档的模型
            （冲量版 5/10/15）则收起数字框、只留那几档 —— 见 `DurationInput`。
          */}
          <Field label="时长" hint={`${modelInfo?.name ?? '当前模型'}支持 ${durMin}-${durMax} 秒`}>
            <DurationInput
              value={form.duration}
              onChange={(n) => patch({ duration: n })}
              presets={durPresets}
              min={durMin}
              max={durMax}
              allowCustom={!modelInfo?.capabilities?.duration_options?.length}
            />
          </Field>
        </CardBody>
      </Card>
    </>
  )

  /* 页签清单 `tabs` 定义在组件上方（原来的位置在这儿）—— 正文取用读那一份，
     **不许在这里再算一次「复刻档少一个」**：同一件事算两遍，迟早出现
     「页签栏 3 个、正文以为有 4 个」。 */

  /* ------------------- 提交栏（每个页签都常驻） ------------------- */

  /**
   * 挂进**右栏最顶上**（`panel` 里排在 `<StudioPanel>` 之前、`.studio__dock` 吸顶），
   * **每一个页签都在** —— 不是隐藏，是所有页签都渲染。
   *
   * 2026-10-09 用户口径：「生成按钮呢？不要只有一个」→ 从「只在最后一个页签出现」
   * 改成**每页常驻**（用户在看第 1 步「输出参数」时找不到按钮）。
   *
   * 被这条改掉的旧口径（2026-09-30）是：「这个（报价 + 生成视频）在最后一个才出现」，
   * 理由是「先出分镜参考图、最后再生成视频，按钮从第 1 步就挂着会抹掉这条流程意图」。
   * 用户看过真机后否掉了它 —— 走到哪一步都可能想直接生成，**找不到按钮**
   * 比「流程意图被抹掉」更难受。
   *
   * 因此它现在与其余 8 个生成页同款（那次全站统一时本条**刻意留着的例外**取消了）：
   * 提交栏从第 1 步就在，与右栏报价同屏。
   *
   * ⚠️ 不再有任何「按页签数算」的判据（早先写 `tabs.length - 1` 只是为了判「是否最后一页」）——
   * 那个「合并页 3 / 复刻档 2」的两档差异坑随之消失。
   *
   * ⚠️ 不可点时的原因按档位给：复刻档是「请先上传参考视频」，合并页是「请先填写产品名称」。
   * `price` 由 `usePricePreview` 现算，与当前停在哪一页无关，所以提前出现不会空着价格。
   */
  const submitDock = (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          disabled={!ready}
          disabledReason={!ready ? (needVideo ? '请先上传参考视频' : '请先填写产品名称') : undefined}
          label="生成视频"
          onGenerate={doGenerate}
        />
        {/*
          「还差什么」常驻一行（2026-10-10 加）。
          ⚠️ 它与上面那个 `disabledReason` 是**两件事**，不能合并：
            · `disabledReason` 只在按钮**按不动**时出现（缺产品名 / 缺参考视频）；
            · 这一行说的是「按钮**按得动**、但按下去会被确认弹窗拦住」——
              也就是那条硬前置（分镜图）。
          产品经理视角自查时发现的真问题正是后者：按钮亮着、点了才在弹窗里看到红字。
          一句话复用 `sbGateReason`、一个字都不重写 —— 它的三种说法（一次没出过 /
          出过但用掉了 / 出过但参数改过）是精心分开的，右栏这里再抄一份必然漂。

          ⚠️ 类名**不复用** `.submit-bar__reason`：那个类在全站的含义是
          「按钮为什么按不动」，`verify-voiceover.py` / `verify-batch-csv.py`
          都拿它的文案当禁用原因读。这里是一个按钮按得动时的**预警**，语义不同。
        */}
        {ready && sbGateReason ? (
          <div className="submit-bar__shortfall text-xs muted">还差：{sbGateReason}</div>
        ) : null}
      </CardBody>
    </Card>
  )

  /**
   * 页签 id → 正文。**键集合必须与 `PANES` 的 id 集合逐项相等**（冒烟钉着这条，
   * 见 smoke-nav 的「生成页骨架」段）—— 少一个键，点过去就是一片空白。
   *
   * ⚠️ 这里是 **`PANES` 的 id 集合**，不是「某一档实际渲染的那几个」：
   * `analyze` 只在复刻档出现、`storyboard` 只在合并页出现，但两个键都必须留着 ——
   * 缺一个，冒烟就报「声明了 5 个页签却有 4 个键」，而那其实是**对的**。
   * 判据是「PANES 里每一页都有正文可渲染」，与某一档用不用它无关。
   *
   * 2026-10-08 由「按下标取数组」改成「按 id 取映射」。两档顺序不同之后，
   * `[stepParams, stepContent, …][step]` 就成了一个**静默错位源**：顺序一改，
   * 第 1 个页签会渲染成第 2 页的正文 —— 页签栏亮着、内容却是别人的，
   * 编译期与类型检查全看不出来（本项目在页签上已经踩过一次同类）。
   * 改成按 id 取之后，**页签怎么排、正文就跟着怎么排**，两者不可能脱钩。
   *
   * ⚠️ 索引写 `tabs.length - 1` 而**不是** `PANES.length - 1`：两档的页签数都比
   * `PANES` 少（合并页 3 / 5、复刻档 2 / 5），按 `PANES` 算时，老 localStorage 里
   * 存着的 `step=4` 会取到 `tabs` 里不存在的项。
   * 末尾 `?? stepParams` 是同一条兜底 —— 失败方向是「回到第 1 页的正文」，
   * 而不是「页签栏亮着、正文一片空白」。
   */
  const PANE_BODIES: Record<string, ReactNode> = {
    params: stepParams,
    /* 复刻档专属两页（第 1 位分析视频 / 第 2 位产品与要求）。
       合并页不出现它们，但键要留着，理由见上 */
    analyze: stepAnalyze,
    brief: stepBrief,
    content: stepContent,
    /* 两档共用（合并页最后一页 / 复刻档中间那一页）—— 2026-10-09 起生成视频
       只认这张九宫格分镜图，所以复刻档也要出（见 `REPLICATE_PANE_ORDER` 沿革）。 */
    storyboard: stepStoryboard,
  }
  const stepBody = PANE_BODIES[tabs[Math.min(step, tabs.length - 1)].id] ?? stepParams

  /**
   * 每页**是不是已经齐了** —— 交给页签条把序号画成对勾（见 `WorkflowTab.done`）。
   *
   * 判据与「能不能提交」同源，不在页签这里另立一套：`content` 用产品名
   * （与 `aiPromptBlocked` / `ready` 同一个字段）、`storyboard` 用 `refPlan.storyboard`
   * （它就是 `sbGateReason` 判空的那一项，且只认「按当前提示词出的那张」）。
   * 两处各判一次的话，会出现「页签打了勾、点生成却被拦」或反过来 —— 那比不打勾更误导。
   *
   * `params` 恒为真：那一页六项**全都有默认值**（平台 / 风格 / 清晰度 / 比例 / 时长
   * 都是预置的，没有必填空项），所以它不可能「缺东西」。写死 `true` 而不是省掉这一项，
   * 是因为省掉之后它会是三页里唯一一个「没有对勾」的，看上去像漏了。
   *
   * 复刻档没有 `content`（它的产品名不单列，见 `stepBrief`），但它有 `brief`
   * —— 用同一份判据（产品名那一格的内容），所以两档都能读。
   */
  const paneDone: Record<string, boolean> = {
    params: true,
    analyze: form.refVideo.length > 0,
    brief: form.sellingPoints.trim().length > 0,
    content: form.productName.trim().length > 0,
    storyboard: refPlan.storyboard,
  }
  /* `{ ...t, done }` 而不是改 `PANES` 本体：`done` 是**运行时**状态，
     塞进模块级常量表就得把表单闭包进去（`PANES` 是纯配置，冒烟直接 import 它）。
     ⚠️ 别写成 `{ id: …, … }` 字面量 —— smoke-nav 按 `{ id: '` 数页签个数，
     在这里多写一个就把「声明了 6 个页签」报成假的。 */
  const tabsWithDone = tabs.map((t) => ({ ...t, done: paneDone[t.id] }))

  return (
    <div className="page page--wide">
      {/*
        档位选择留在页签外：它决定整条工作流（场景清单 / 时长 / 比例 / 默认平台），
        而且**换档 = 换一整批参数**（见 switchMode），不属于任何一个页签里的参数。
        合并前这是两条侧栏入口（带货短视频 / 商品视频）—— 同一批决策要用户自己
        想清楚去哪个页面，进来还得重新选一遍平台与时长。
        视频复刻没有这一段：它是独立页，输入物相反（必传参考视频）。
      */}
      {/*
        档位 chip-row —— **2026-10-05 起不再出现**：`short-video` 与 `main-video`
        并成了一档，`STUDIO_VIDEO_MODES` 只剩一条。判据写成「由清单长度派生」
        而不是直接删掉这段：一个 chip 的 chip-row 没有意义，但清单一旦重新分档，
        这段自动回来，页面不用改。同源判据见 config/videoModes.ts 头部说明。
      */}
      {!isReplicate && STUDIO_VIDEO_MODES.length > 1 ? (
        <Chips
          className="chip-row"
          style={{ marginBottom: 12 }}
          value={mode}
          onChange={switchMode}
          options={STUDIO_VIDEO_MODES.map((m) => ({ value: m.id, label: m.name }))}
          titles={Object.fromEntries(STUDIO_VIDEO_MODES.map((m) => [m.id, m.desc]))}
        />
      ) : null}

      {/*
        模板行与档位 chip-row 同为**根决策**，都在页签外，且都在左栏上方。
        视频复刻不渲染它：那一档的输入物是参考视频（复刻既有结构），
        「场景 / 风格 / 时长」这套预设对复刻没有意义。
      */}
      {!isReplicate ? (
        <TemplateRail
          mode={mode}
          custom={customTpls}
          current={tpl}
          onPick={pickTemplate}
          onPreview={setPreviewTpl}
          /* 与分镜页那颗「存为模板」是**同一个入口**（都调 `openSaveTemplate`）——
             两处各写一遍的话，迟早一处带封面、另一处忘带。 */
          onSave={openSaveTemplate}
          /*
            生成引擎 —— 从「引擎与素材」页搬来（2026-10-09，用户口径「引擎放到视频模板那边」）。
            仍是一行下拉：把清单里的模型铺成卡片（旧 `ModelSelect variant="cards"`）在
            1482px 窗口里量到 **1205px 高**，把模板轨道与提交栏全顶到折线以下（用户原话
            「下拉框就可以了，现在太占位置」）。单价没丢 —— 写在每个 option 文本里，
            通道徽标与备注在控件下方 hint。
          */
          engine={
            <ModelSelect
              kind="video"
              label="生成引擎"
              value={form.model}
              onChange={(v) => patch({ model: v })}
              hasRef={hasRef}
            />
          }
        />
      ) : null}

      <GeneratorFrame
        form={
          <WorkflowTabs tabs={tabsWithDone} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              onClear={clearResults}
              activeHint="视频生成较慢，任务在后台跑，切页不中断"
              resultTitle={`${meta.name} 结果`}
              /* 标题跟着当前档走。注意两栏的**取数**已经不是「按当前档收窄」了 ——
                 并档后 `slugs` 一次带上 short-video 与 main-video 两个 slug（见上面那段注释），
                 所以这里说的是「这一栏归哪一档管」，不是「只显示哪一档的记录」。 */
              historyTitle={meta.name}
              resultEmpty="确认参数后点「生成视频」。完成后结果会在这里和「我的作品」同时出现。"
              aspectRatio={form.aspectRatio}
              historyTabs={historyTabs}
              /* 历史记录上的「重做」把该次参数填回左栏（见 lib/refill.ts） */
              onRedo={(job: Job) => {
                patch(refillFromInputs(form, job.inputs))
                toast.success('已把这次任务的参数填回左侧表单 —— 确认后可再次生成')
              }}
              /*
                2026-10-09：这一栏原来挂着一张「成片提示词」卡（2026-10-08 从底部
                「分镜脚本」卡升上来的）。用户口径「AI 成片提示词放到确认弹窗」→
                整块**搬进「生成视频」的确认框**，右栏不再常驻它（见 `doGenerate` 的
                `info.promptAi` 与 `components/studio.tsx` 的 `runAiPrompt`）。
                搬走之后右栏只剩「进行中任务 / 结果 / 历史」三段，页面上再无第三处
                提示词出口 —— 与「提示词唯一展示处是确认框」这条约定更贴。
              */
            />
          </>
        }
      />

      <PriceConfirmDialog
        info={flow.pending}
        onCancel={flow.cancel}
        onConfirm={(edits) => {
          /*
           * ⚠️ **`edits` 必须原样透传给 `flow.confirm`**（2026-10-08 加）。
           * 本页是全站唯一把 `onConfirm` 包了一层的调用方；包一层之后参数就断了 ——
           * 而确认弹窗里那段提示词现在是**可编辑**的（`<Textarea data-testid="base-prompt">`），
           * 丢掉 `edits` 的表现是：用户改完点确认，提交成功、图照出、钱照扣，
           * 只有内容不是他改过的那一版。这类「预览与实发不是同一份」
           * 在花钱按钮前面最不该破。
           */
          /*
           * 「分镜图用掉就作废」现在记在 `doGenerate` 的 `onDone` 里 —— **出片之后**才记，
           * 失败 / 取消不算（2026-10-10 改，理由见那边的注释）。
           * ⚠️ 别在这儿补回来：记在「点确认」这一刻，会让任务被取消 / 失败后
           * 「失败不扣费，可直接重试」这句话再次自相矛盾。
           */
          void flow.confirm(edits)
        }}
      />

      {/*
        模板预览抽屉。挂在页面根节点、不放进卡片 —— 卡片是 `overflow: hidden`，
        弹层万一被裁就是「点了没反应」。它只在点了卡片上那颗播放按钮时才有内容。
      */}
      <TemplatePreview
        tpl={previewTpl}
        onClose={() => setPreviewTpl(null)}
        onUse={(t) => {
          pickTemplate(t)
          setPreviewTpl(null)
        }}
        /* 自存模板还没有成片时，抽屉里出现「生成案例片」（见 `generateSample`）——
           工作台有产品 / 卖点 / 模型这套上下文，才给得出这颗键。 */
        onGenerateSample={(t) => void generateSample(t)}
        /* 抽屉规格栏那行「生成引擎」—— 名字在这里查好再传（抽屉不认识模型清单）。
           点「用这个模板」会把引擎一起换掉（见 `pickTemplate`），
           而抽屉正是那次点击发生的地方，不说清换成谁就是一个静默的副作用。 */
        engineName={
          previewTpl?.recommendedModel
            ? listModels('video').find((m) => m.id === previewTpl.recommendedModel)?.name ??
              previewTpl.recommendedModel
            : undefined
        }
      />

      {/*
        分镜九宫格的放大遮罩（2026-10-08 用户口径：「可以点开」）。

        ⚠️ 只有一张图也要走 `ImageLightbox` 而不是自己写个 `<img>` 铺满屏：
        点背景关闭、Esc 关闭、锁背景滚动、portal 到 body（卡片 `overflow: hidden`
        会把遮罩裁掉）这四件事在任何图片查看器里都是同一套，本项目里它们
        只有这一份实现。`index` 恒为 0、`onIndex` 给空实现 ——
        单图没有「上一张 / 下一张」，`total === 1` 时那两个箭头本来就不渲染。
      */}
      {sbZoom ? (
        <ImageLightbox
          items={[{ url: sbUrl, type: 'image', thumbUrl: sbUrl, text: '九宫格分镜参考图' }]}
          index={0}
          onIndex={() => {}}
          onClose={() => setSbZoom(false)}
          title="分镜参考图"
        />
      ) : null}

      {/*
        「存为模板」弹窗。**它只收集名字 / 角标 / 说明三件事** ——
        参数、分镜、封面、成片样例全在这里抽好交进去（见 `saveTemplate`）。
        让弹窗自己去读表单是错的：用户完全可以开着弹窗去改参数（同一屏就能改），
        那样「存的是哪一套」就取决于弹窗挂载那一刻表单长什么样。

        也挂在页面根节点：卡片是 `overflow: hidden`，弹层放进去会被裁。
      */}
      <SaveTemplateDialog
        open={saveTplOpen}
        onClose={() => setSaveTplOpen(false)}
        /* 产品名优先 —— 用户在模板条里认卡片，多半是按「这是那个榨汁杯的」认的。
           没填产品名才退回当前模板名（此时他必然是点了某张模板铺开来的）。 */
        initial={{
          name: form.productName.trim() || (tpl ? templateTitle(tpl) : ''),
          /* 角标留空：工作台的模板卡只有 84px 宽，「我的模板 · 」这 7 个字是白吃的 */
          tag: '',
          description: '',
          /* 模板内容两块的初值 = **当前这套实际会发出去的那一份**
             （`storyboard` 是叠好镜头细化的结果，`tpl.shot` 是当前命中模板的全片约束）。
             不空着让用户从零写：多数情况下他要的只是把其中两条改得更具体，
             给一份空的等于先把手上有的东西扔掉再让他重写一遍。 */
          shots: storyboard.map((s) => ({ t: s.t, d: s.d })),
          shot: tpl?.shot ?? '',
        }}
        coverUrl={sbUrl}
        detail={{
          summary: `${form.scene} · ${form.style} · ${form.duration}s · ${form.aspectRatio}`,
          hasSample: !!sampleVideoUrl,
        }}
        onSubmit={saveTemplate}
        /* 两块内容各一颗 AI 扩写。只有工作台这两处能传 ——
           模板中心那页没有「这支片子拍什么」的上下文（见组件的 `onAiShots` 注释） */
        onAiShots={aiExpandShots}
        onAiShot={aiExpandShot}
      />
    </div>
  )
}
